import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import glbCache from './glb-cache.js';

// Avatar Application - Complete with lipsync, blink, and smile animations

// API URL Configuration - Works with localhost (dev) and IP/domain (production)
// Always use same-origin to ensure session cookies are sent correctly
const API_BASE_URL = `${window.location.protocol}//${window.location.host}`;

// In-memory session ID — generated once per page load, shared across all instances.
// Not stored in cookies or localStorage (both blocked in cross-origin iframes).
const CLIENT_SESSION_ID = 'session-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);

/**
 * Centralized API fetch utility with cache control
 * More efficient than adding headers to every fetch call
 */
const apiClient = {
    /**
     * GET request with cache busting
     */
    get: async (url, options = {}) => {
        const timestamp = Date.now();
        const cacheBustedUrl = url.includes('?') ? `${url}&_t=${timestamp}` : `${url}?_t=${timestamp}`;

        return fetch(cacheBustedUrl, {
            method: 'GET',
            cache: 'no-store',
            headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
                'Expires': '0',
                ...options.headers
            },
            credentials: 'include',
            ...options
        });
    },

    /**
     * POST request with cache control
     */
    post: async (url, body, options = {}) => {
        return fetch(url, {
            method: 'POST',
            cache: 'no-store',
            headers: {
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
                'Expires': '0',
                ...options.headers
            },
            body: JSON.stringify(body),
            credentials: 'include',
            redirect: 'follow',
            ...options
        });
    }
};

class AvatarApplication {
    constructor() {
        // Alias mapping: gender/friendly names → actual avatar names
        const avatarAliases = { 'male': 'kevin', 'female': 'lisa', 'kevin-full-body': 'full-body-kevin', 'lisa-full-body': 'full-body-lisa' };

        // Avatar configuration - Check URL path first (e.g. /lisa, /male), then query params, then localStorage, then default
        const pathSegment = window.location.pathname.replace(/^\//, '').split('/')[0].toLowerCase();
        // Only use path segment if it's not a known static/api path
        const reservedPaths = ['models', 'voice', 'assets', 'public', 'avatar', 'avatars', 'talk', 'stop', 'config', ''];
        const rawName = (!reservedPaths.includes(pathSegment) && pathSegment) ? pathSegment : null;
        const avatarFromPath = rawName ? (avatarAliases[rawName] || rawName) : null;
        const urlParams = new URLSearchParams(window.location.search);
        const rawParam = urlParams.get('avatar');
        const avatarFromParam = rawParam ? (avatarAliases[rawParam.toLowerCase()] || rawParam.toLowerCase()) : null;
        this.avatarName = avatarFromPath || avatarFromParam || localStorage.getItem('avatar_name') || 'lisa';
        this.avatarConfig = null; // Will be loaded from server

        console.log(`🎭 Initializing avatar: ${this.avatarName}`);

        // Configuration options
        this.opt = {
            modelRoot: "Armature",
            modelPixelRatio: 1,
            modelFPS: 30,
            modelMovementFactor: 1,
            cameraView: 'full',
            cameraDistance: 0,
            cameraX: 0,
            cameraY: 0,
            cameraRotateX: 0,
            cameraRotateY: 0,
            cameraRotateEnable: false,
            cameraPanEnable: false,
            cameraZoomEnable: false,
            lightAmbientColor: '#ffffff',
            lightAmbientIntensity: 1.5,
            lightDirectColor: '#dcd0d0',
            lightDirectIntensity: 5.5,
            lightDirectPhi: 1.5,
            lightDirectTheta: 1.5,
            lightSpotIntensity: 0,
            lightSpotColor: 0x3388ff,
            lightSpotPhi: 0.1,
            lightSpotTheta: 4,
            lightSpotDispersion: 1,
            avatarMood: "neutral",
            avatarMute: false,
            markedOptions: { mangle: false, headerIds: false, breaks: true },
            statsNode: null,
            statsStyle: null
        };

        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.controls = null;
        this.clock = null;
        this.character = null;
        this.mixer = null;
        this.animations = null;
        this.morphs = [];
        this.activeSpeechAudios = [];
        this.currentSpeechAudio = null;
        this.currentBlendData = null;
        this.isSpeaking = false;
        this.currentSpeechText = null; // Track current speech text to prevent duplicates
        this.speechQueue = []; // Queue for managing multiple speech requests
        this.isProcessingQueue = false; // Flag to track if queue is being processed

        // Idle pool animation switching
        this.idlePoolTimeout = null;
        this.activeIdleAction = null;
        this.currentIdleAnimIndex = null;

        this.animClock = 0;
        this.animTimeLast = 0;
        this.animFrameDur = 1000 / this.opt.modelFPS; // Use opt.modelFPS
        this.animSlowdownRate = 1;
        this.animQueue = [];
        this.animBaseline = {};

        // Light references for dynamic updates
        this.lightAmbient = null;
        this.lightDirect = null;
        this.lightSpot = null;
        this.lightKey = null;
        this.lightFill = null;
        this.lightRim = null;

        // IK (Inverse Kinematics) Mesh for advanced posing
        this.ikMesh = new THREE.SkinnedMesh();
        this.ikSetup = {
            'LeftShoulder': null, 'LeftArm': 'LeftShoulder', 'LeftForeArm': 'LeftArm',
            'LeftHand': 'LeftForeArm', 'LeftHandMiddle1': 'LeftHand',
            'RightShoulder': null, 'RightArm': 'RightShoulder', 'RightForeArm': 'RightArm',
            'RightHand': 'RightForeArm', 'RightHandMiddle1': 'RightHand'
        };
        this.ikBones = [];
        this.initializeIK();

        // Pose system
        this.poseName = "side"; // Current pose name
        this.poseWeightOnLeft = true; // Weight distribution
        this.poseCurrentTemplate = null;
        this.poseBase = null;
        this.poseTarget = null;
        this.poseAvatar = null;
        this.posePropNames = [];
        this.poseDelta = null;
        this.armature = null; // Set when model loads

        // Initialize pose templates
        this.initializePoseTemplates();

        // Animation state
        this.stateName = 'idle';
        this.animFixed = {};

        this.mapping = {
            mouthRollLower: "Mouth_Roll_In_Lower_L",
            mouthRollUpper: "Mouth_Roll_In_Upper_R",
            mouthStretchLeft: "Mouth_Stretch_L",
            mouthStretchRight: "Mouth_Stretch_R",
            mouthSmile: "ShapeKey_Smile_Wide_Closed",
            browDownLeft: "Brow_Drop_L",
            browDownRight: "Brow_Drop_R",
            browInnerUp: "Brow_Raise_Inner_L",
            browOuterUpLeft: "Brow_Raise_Outer_L",
            browOuterUpRight: "Brow_Raise_Outer_R",
        };

        // animMoods loaded entirely from avatars.json (per-avatar config)
        this.animMoods = null;
        this.moodName = this.opt.avatarMood || "neutral";
        this.mood = null;

        // Randomized morph targets for natural variation (from Experience 21.js)
        // this.randomized = [
        //     'noseSneerLeft', 'noseSneerRight',
        //     'Brow_Drop_L', 'Brow_Drop_R',
        //     'Brow_Raise_Outer_L', 'Brow_Raise_Outer_R',
        //     'cheekPuff', 'cheekSquintLeft', 'cheekSquintRight'
        // ];

        // Easing function using sigmoid (from Experience 21.js)
        this.easing = this.sigmoidFactory(5);

        this.loadAvatarConfig().then(() => {
            this.init();
            this.setupMessageListener();
            this.animate(performance.now());
        });
    }

    /**
     * Load avatar configuration from server
     */
    async loadAvatarConfig() {
        try {
            const response = await apiClient.get(`${API_BASE_URL}/avatar/${this.avatarName}`);
            if (!response.ok) {
                throw new Error('Failed to load avatar configuration');
            }
            this.avatarConfig = await response.json();

            // Update mapping from config
            if (this.avatarConfig.morphTargets && this.avatarConfig.morphTargets.expressions) {
                this.mapping = this.avatarConfig.morphTargets.expressions;
            }
            // Build animMoods dynamically from morphTargets config
            this.buildAnimMoods();
            this.mood = this.animMoods[this.moodName] || this.animMoods['neutral'];
        } catch (error) {
            console.error('❌ Error loading avatar config:', error);
        }
    }

    /**
     * Build animMoods dynamically from morphTargets in avatarConfig.
     * Uses morph target names from config so no per-avatar animationMoods block is needed.
     */
    buildAnimMoods() {
        const mt = this.avatarConfig?.morphTargets || {};
        const blinkL = mt.blink?.left || 'Eye_Blink_L.001';
        const blinkR = mt.blink?.right || 'Eye_Blink_R.001';
        const smile = mt.smile || 'smile02.001';
        const squintL = mt.eyeMovement?.squintLeft || 'eyeSquintLeft';
        const squintR = mt.eyeMovement?.squintRight || 'eyeSquintRight';
        const browInnerL = mt.expressions?.browInnerUpLeft || 'Brow_Raise_Inner_L';
        const browOuterL = mt.expressions?.browOuterUpLeft || 'Brow_Raise_Outer_L';
        const browOuterR = mt.expressions?.browOuterUpRight || 'Brow_Raise_Outer_R';
        const noseSneerL = mt.misc?.noseSneerLeft || 'Nose_Sneer_L';
        const noseSneerR = mt.misc?.noseSneerRight || 'Nose_Sneer_R';
        const browDropL = mt.expressions?.browDownLeft || 'Brow_Drop_L';
        const browDropR = mt.expressions?.browDownRight || 'Brow_Drop_R';
        const browInnerR = mt.expressions?.browInnerUpRight || 'Brow_Raise_Inner_R';
        const cheekPuff = mt.misc?.cheekPuff || 'Cheek_Puff_R';
        const cheekSquintL = mt.misc?.cheekSquintLeft || 'Cheek_Raise_L';
        const cheekSquintR = mt.misc?.cheekSquintRight || 'Cheek_Raise_R';

        const blinkVs = {};
        blinkVs[blinkL] = [1, 1, 0];
        blinkVs[blinkR] = [1, 1, 0];

        const mouthVs = {};
        mouthVs[smile] = [[0.2, 0.5], [0.6, 0.8, 3]];

        const miscVs = {};
        miscVs[squintL] = [[0, 0.3, 3]];
        miscVs[squintR] = [[0, 0.3, 3]];
        miscVs[browInnerL] = [[0, 0.6, 2]];
        miscVs[browOuterL] = [[0, 0.6, 2]];
        miscVs[browOuterR] = [[0, 0.6, 3]];

        const noseVs = {};
        // noseVs[noseSneerL] = [[0.4, 0.6, 3]];
        noseVs[noseSneerR] = [[0.2, 0.5, 1]];


        const browVs = {}
        browVs[browDropL] = [[0.3, 0.6, 3]];
        // browVs[browDropR] = [[0.3, 0.6, 1]];
        browVs[browInnerR] = [[0, 0.6, 4]];

        const cheekVs = {}
        cheekVs[cheekSquintL] = [[0.5, 0.8, 2]];
        cheekVs[cheekSquintR] = [[0.5, 0.8, 1]];


        // Build baseline dynamically from morphTargets config
        // Values are configurable per avatar in avatars.json → morphTargets.baseline
        const bl = mt.baseline || {};
        const lookDown = mt.eyeMovement?.lookDown || 'eyesLookDown';
        const baselineObj = {};
        baselineObj[squintL] = bl.squintLeft ?? 0.1;
        baselineObj[squintR] = bl.squintRight ?? 0.1;
        baselineObj[lookDown] = bl.lookDown ?? 0.1;

        this.animMoods = {
            'neutral': {
                baseline: baselineObj,
                speech: { deltaRate: 0, deltaPitch: 0, deltaVolume: 0 },
                anims: [
                    { name: 'breathing', delay: 1500, dt: [1200, 500, 1000], vs: { chestInhale: [0.5, 0.5, 0] } },
                    {
                        name: 'pose', alt: [
                            { p: 0.4, delay: [5000, 20000], vs: { pose: ['side'] } },
                            { p: 0.4, delay: [5000, 20000], vs: { pose: ['hip'] } },
                            { delay: [5000, 20000], vs: { pose: ['straight'] } }
                        ]
                    },
                    {
                        name: 'head',
                        idle: { delay: [0, 1000], dt: [[200, 5000]], vs: { headRotateX: [[-0.04, 0.10]], headRotateY: [[-0.3, 0.3]], headRotateZ: [[-0.08, 0.08]] } },
                        talking: { dt: [[0, 1000, 0]], vs: { headRotateX: [[-0.05, 0.15, 1, 2]], headRotateY: [[-0.1, 0.1]], headRotateZ: [[-0.1, 0.1]] } }
                    },
                    { name: 'eyes', delay: [200, 5000], dt: [[100, 500], [100, 5000, 2]], vs: { eyesRotateY: [[-0.6, 0.6]], eyesRotateX: [[-0.2, 0.6]] } },
                    { name: 'blink', delay: [1000, 8000, 1, 2], dt: [50, [100, 300], 100], vs: blinkVs },
                    { name: 'mouth', delay: [1000, 5500], dt: [[300, 800], [600, 6500, 2]], vs: mouthVs },
                    { name: 'misc', delay: [100, 5000], dt: [[100, 500], [100, 5000, 2]], vs: miscVs },
                    { name: 'nose', delay: [1500, 5000], dt: [[100, 600], [500, 1500, 2]], vs: noseVs },
                    { name: 'brow', delay: [2000, 5000], dt: [[100, 600], [500, 1500, 2]], vs: browVs },
                    { name: 'cheek', delay: [2500, 5000], dt: [[100, 600], [500, 1500, 2]], vs: cheekVs }
                ]
            }
        };

        console.log(`🎭 Built animMoods for ${this.avatarName} using morphTargets: blink=[${blinkL}, ${blinkR}], smile=${smile}`);
    }

    /**
     * Initialize Inverse Kinematics mesh and bones
     */
    initializeIK() {
        Object.entries(this.ikSetup).forEach((e, i) => {
            const bone = new THREE.Bone();
            bone.name = e[0];
            if (e[1]) {
                this.ikMesh.getObjectByName(e[1]).add(bone);
            } else {
                this.ikMesh.add(bone);
            }
            this.ikBones.push(bone);
        });
        this.ikMesh.bind(new THREE.Skeleton(this.ikBones));
    }

    /**
     * Initialize pose templates for body positioning
     * NOTE: Due to size, only essential poses (side, hip, straight, sitting) are included
     * Full pose library can be added as needed
     */
    initializePoseTemplates() {
        this.poseTemplates = {
            'side': {
                standing: true,
                props: {
                    'Hips.position': { x: 0, y: 1, z: 0 }, 'Hips.rotation': { x: -0.003, y: -0.017, z: 0.1 }, 'Spine.rotation': { x: -0.103, y: -0.002, z: -0.063 }, 'Spine1.rotation': { x: 0.042, y: -0.02, z: -0.069 }, 'Spine2.rotation': { x: 0.131, y: -0.012, z: -0.065 }, 'Neck.rotation': { x: 0.027, y: 0.006, z: 0 }, 'Head.rotation': { x: 0.077, y: -0.065, z: 0 }, 'LeftShoulder.rotation': { x: 1.599, y: 0.084, z: -1.77 }, 'LeftArm.rotation': { x: 1.364, y: 0.052, z: -0.044 }, 'LeftForeArm.rotation': { x: 0.002, y: -0.007, z: 0.331 }, 'LeftHand.rotation': { x: 0.104, y: -0.067, z: -0.174 }, 'RightShoulder.rotation': { x: 1.541, y: 0.192, z: 1.775 }, 'RightArm.rotation': { x: 1.273, y: -0.352, z: -0.067 }, 'RightForeArm.rotation': { x: -0.011, y: -0.031, z: -0.357 }, 'RightHand.rotation': { x: -0.008, y: 0.312, z: -0.028 }, 'LeftUpLeg.rotation': { x: 0.096, y: 0.209, z: 2.983 }, 'LeftLeg.rotation': { x: -0.053, y: 0.042, z: -0.017 }, 'LeftFoot.rotation': { x: 1.091, y: 0.15, z: 0.026 }, 'LeftToeBase.rotation': { x: 0.469, y: -0.07, z: -0.015 }, 'RightUpLeg.rotation': { x: -0.307, y: -0.219, z: 2.912 }, 'RightLeg.rotation': { x: -0.359, y: 0.164, z: 0.015 }, 'RightFoot.rotation': { x: 1.035, y: 0.11, z: 0.005 }, 'RightToeBase.rotation': { x: 0.467, y: 0.07, z: 0.015 }
                }
            },
            'hip': {
                standing: true,
                props: {
                    'Hips.position': { x: 0, y: 1, z: 0 }, 'Hips.rotation': { x: -0.036, y: 0.09, z: 0.135 }, 'Spine.rotation': { x: 0.076, y: -0.035, z: 0.01 }, 'Spine1.rotation': { x: -0.096, y: 0.013, z: -0.094 }, 'Spine2.rotation': { x: -0.014, y: 0.002, z: -0.097 }, 'Neck.rotation': { x: 0.034, y: -0.051, z: -0.075 }, 'Head.rotation': { x: 0.298, y: -0.1, z: 0.154 }, 'LeftShoulder.rotation': { x: 1.694, y: 0.011, z: -1.68 }, 'LeftArm.rotation': { x: 1.343, y: 0.177, z: -0.153 }, 'LeftForeArm.rotation': { x: -0.049, y: 0.134, z: 0.351 }, 'LeftHand.rotation': { x: 0.057, y: -0.189, z: -0.026 }, 'RightShoulder.rotation': { x: 1.597, y: 0.012, z: 1.816 }, 'RightArm.rotation': { x: 0.618, y: -1.274, z: -0.266 }, 'RightForeArm.rotation': { x: -0.395, y: -0.097, z: -1.342 }, 'RightHand.rotation': { x: -0.816, y: -0.057, z: -0.976 }, 'LeftUpLeg.rotation': { x: -0.095, y: -0.058, z: -3.338 }, 'LeftLeg.rotation': { x: -0.366, y: 0.287, z: -0.021 }, 'LeftFoot.rotation': { x: 1.131, y: 0.21, z: 0.176 }, 'LeftToeBase.rotation': { x: 0.739, y: -0.068, z: -0.001 }, 'RightUpLeg.rotation': { x: -0.502, y: 0.362, z: 3.153 }, 'RightLeg.rotation': { x: -1.002, y: 0.109, z: 0.008 }, 'RightFoot.rotation': { x: 0.626, y: -0.097, z: -0.194 }, 'RightToeBase.rotation': { x: 1.33, y: 0.288, z: -0.078 }
                }
            },
            'straight': {
                standing: true,
                props: {
                    'Hips.position': { x: 0, y: 0.989, z: 0.001 }, 'Hips.rotation': { x: 0.047, y: 0.007, z: -0.007 }, 'Spine.rotation': { x: -0.143, y: -0.007, z: 0.005 }, 'Spine1.rotation': { x: -0.043, y: -0.014, z: 0.012 }, 'Spine2.rotation': { x: 0.072, y: -0.013, z: 0.013 }, 'Neck.rotation': { x: 0.048, y: -0.003, z: 0.012 }, 'Head.rotation': { x: 0.05, y: -0.02, z: -0.017 }, 'LeftShoulder.rotation': { x: 1.62, y: -0.166, z: -1.605 }, 'LeftArm.rotation': { x: 1.275, y: 0.544, z: -0.092 }, 'LeftForeArm.rotation': { x: 0, y: 0, z: 0.302 }, 'LeftHand.rotation': { x: -0.225, y: -0.154, z: 0.11 }, 'RightShoulder.rotation': { x: 1.615, y: 0.064, z: 1.53 }, 'RightArm.rotation': { x: 1.313, y: -0.424, z: 0.131 }, 'RightForeArm.rotation': { x: 0, y: 0, z: -0.317 }, 'RightHand.rotation': { x: -0.158, y: -0.639, z: -0.196 }, 'LeftUpLeg.rotation': { x: -0.077, y: -0.058, z: 3.126 }, 'LeftLeg.rotation': { x: -0.252, y: 0.001, z: -0.018 }, 'LeftFoot.rotation': { x: 1.315, y: -0.064, z: 0.315 }, 'LeftToeBase.rotation': { x: 0.577, y: -0.07, z: -0.009 }, 'RightUpLeg.rotation': { x: -0.083, y: -0.032, z: 3.124 }, 'RightLeg.rotation': { x: -0.272, y: -0.003, z: 0.021 }, 'RightFoot.rotation': { x: 1.342, y: 0.076, z: -0.222 }, 'RightToeBase.rotation': { x: 0.44, y: 0.069, z: 0.016 }
                }
            },
            'sitting': {
                sitting: true, lying: true,
                props: {
                    'Hips.position': { x: 0, y: 0.117, z: 0.005 }, 'Hips.rotation': { x: -0.411, y: -0.049, z: 0.056 }, 'Spine.rotation': { x: 0.45, y: -0.039, z: -0.116 }, 'Spine1.rotation': { x: 0.092, y: -0.076, z: 0.08 }, 'Spine2.rotation': { x: 0.073, y: 0.035, z: 0.066 }, 'Neck.rotation': { x: 0.051, y: 0.053, z: -0.079 }, 'Head.rotation': { x: -0.169, y: 0.009, z: 0.034 }, 'LeftShoulder.rotation': { x: 1.756, y: -0.037, z: -1.301 }, 'LeftArm.rotation': { x: -0.098, y: 0.016, z: 1.006 }, 'LeftForeArm.rotation': { x: -0.089, y: 0.08, z: 0.837 }, 'LeftHand.rotation': { x: 0.262, y: -0.399, z: 0.3 }, 'RightShoulder.rotation': { x: 1.619, y: -0.139, z: 1.179 }, 'RightArm.rotation': { x: 0.17, y: -0.037, z: -1.07 }, 'RightForeArm.rotation': { x: -0.044, y: -0.056, z: -0.665 }, 'RightHand.rotation': { x: 0.278, y: 0.454, z: -0.253 }, 'LeftUpLeg.rotation': { x: -1.957, y: 0.083, z: -2.886 }, 'LeftLeg.rotation': { x: -1.46, y: 0.123, z: 0.005 }, 'LeftFoot.rotation': { x: -0.013, y: 0.016, z: 0.09 }, 'LeftToeBase.rotation': { x: 0.744, y: 0, z: 0 }, 'RightUpLeg.rotation': { x: -1.994, y: 0.125, z: 2.905 }, 'RightLeg.rotation': { x: -1.5, y: -0.202, z: -0.006 }, 'RightFoot.rotation': { x: -0.012, y: -0.065, z: 0.081 }, 'RightToeBase.rotation': { x: 0.758, y: 0, z: 0 }
                }
            }
        };

        // Convert all pose templates to THREE objects
        Object.values(this.poseTemplates).forEach(x => {
            x.props = this.propsToThreeObjects(x.props);
        });

        // Initialize pose deltas
        this.poseDelta = {
            props: {
                'Hips.quaternion': { x: 0, y: 0, z: 0 }, 'Spine.quaternion': { x: 0, y: 0, z: 0 },
                'Spine1.quaternion': { x: 0, y: 0, z: 0 }, 'Neck.quaternion': { x: 0, y: 0, z: 0 },
                'Head.quaternion': { x: 0, y: 0, z: 0 }
            }
        };

        // Add legs, arms and hands
        ['Left', 'Right'].forEach(x => {
            ['Leg', 'UpLeg', 'Arm', 'ForeArm', 'Hand'].forEach(y => {
                this.poseDelta.props[x + y + '.quaternion'] = { x: 0, y: 0, z: 0 };
            });
        });

        // Collect all property names
        const names = new Set();
        Object.values(this.poseTemplates).forEach(x => {
            Object.keys(x.props).forEach(y => names.add(y));
        });
        Object.keys(this.poseDelta.props).forEach(x => {
            names.add(x);
        });
        this.posePropNames = [...names];

        // Initialize base poses
        this.poseBase = this.poseFactory(this.poseTemplates[this.poseName]);
        this.poseTarget = this.poseFactory(this.poseTemplates[this.poseName]);
    }

    /**
     * Convert pose properties to THREE.js objects
     * @param {Object} p - Pose properties
     * @return {Object} Converted properties
     */
    propsToThreeObjects(p) {
        const r = {};
        for (let [key, val] of Object.entries(p)) {
            const ids = key.split('.');
            let v;
            if (ids[1] === 'position' || ids[1] === 'scale') {
                v = new THREE.Vector3(val.x, val.y, val.z);
            } else if (ids[1] === 'rotation') {
                key = ids[0] + '.quaternion';
                v = new THREE.Quaternion().setFromEuler(new THREE.Euler(val.x, val.y, val.z, 'XYZ')).normalize();
            } else if (ids[1] === 'quaternion') {
                v = new THREE.Quaternion(val.x, val.y, val.z, val.w).normalize();
            }
            if (v) {
                r[key] = v;
            }
        }
        return r;
    }

    /**
     * Create a pose object from template
     * @param {Object} template - Pose template
     * @param {number} duration - Transition duration
     * @return {Object} Pose object
     */
    poseFactory(template, duration) {
        const o = { template: template, props: {} };
        Object.entries(template.props).forEach(x => {
            o.props[x[0]] = x[1].clone();

            // Restrain movement when standing (from Experience 21.js)
            if (this.opt.modelMovementFactor < 1 && template.standing &&
                (x[0] === 'Hips.quaternion' || x[0] === 'Spine.quaternion' ||
                    x[0] === 'Spine1.quaternion' || x[0] === 'Spine2.quaternion' ||
                    x[0] === 'Neck.quaternion' || x[0] === 'LeftUpLeg.quaternion' ||
                    x[0] === 'LeftLeg.quaternion' || x[0] === 'RightUpLeg.quaternion' ||
                    x[0] === 'RightLeg.quaternion')) {
                const ref = this.poseTemplates["straight"].props[x[0]];
                const angle = o.props[x[0]].angleTo(ref);
                o.props[x[0]].rotateTowards(ref, (1 - this.opt.modelMovementFactor) * angle);
            }

            // Custom properties
            o.props[x[0]].t = this.animClock; // timestamp
            o.props[x[0]].d = duration; // Transition duration
        });
        this.poseWeightOnLeft = true;
        return o;
    }

    /**
     * Set pose from template
     * @param {Object} template - Pose template to apply
     */
    setPoseFromTemplate(template) {
        const isIntermediate = this.poseTarget && this.poseTarget.template &&
            ((this.poseTarget.template.standing && template.lying) ||
                (this.poseTarget.template.lying && template.standing));
        const isSameTemplate = template === this.poseCurrentTemplate;
        const isWeightOnLeft = this.poseWeightOnLeft;
        let duration = isIntermediate ? 1000 : 2000;

        // New pose template
        if (isIntermediate) {
            this.poseCurrentTemplate = this.poseTemplates['side'];
            setTimeout(() => {
                this.setPoseFromTemplate(template);
            }, duration);
        } else {
            this.poseCurrentTemplate = template;
        }

        // Set target
        this.poseTarget = this.poseFactory(this.poseCurrentTemplate, duration);

        // Make sure deltas are included in the target
        Object.keys(this.poseDelta.props).forEach(key => {
            if (!this.poseTarget.props.hasOwnProperty(key)) {
                this.poseTarget.props[key] = this.poseDelta.props[key];
            }
        });
    }

    /**
     * Change avatar pose
     * @param {string} poseName - Name of the pose ('side', 'hip', 'straight', 'sitting')
     */
    changePose(poseName) {
        if (this.poseTemplates[poseName]) {
            this.poseName = poseName;
            this.setPoseFromTemplate(this.poseTemplates[poseName]);
        }
    }

    /**
     * Update pose base with smooth transitions (from Experience 21.js)
     * @param {number} t - Current animation clock time
     */
    updatePoseBase(t) {
        if (!this.poseTarget || !this.poseAvatar || !this.poseBase) return;

        for (const [key, v] of Object.entries(this.poseTarget.props)) {
            const o = this.poseAvatar.props[key];
            if (o) {
                const alpha = (t - v.t) / v.d;
                if (alpha > 1 || !this.poseBase.props.hasOwnProperty(key)) {
                    o.copy(v);
                } else {
                    if (o.isQuaternion) {
                        o.copy(this.poseBase.props[key].slerp(v, this.easing(alpha)));
                    } else if (o.isVector3) {
                        o.copy(this.poseBase.props[key].lerp(v, this.easing(alpha)));
                    }
                }
            }
        }
    }

    /**
     * Update avatar pose deltas (from Experience 21.js)
     */
    updatePoseDelta() {
        if (!this.poseDelta || !this.poseAvatar) return;

        for (const [key, d] of Object.entries(this.poseDelta.props)) {
            const e = new THREE.Euler(d.x, d.y, d.z);
            const o = this.poseAvatar.props[key];
            if (o !== undefined) {
                if (o.isQuaternion) {
                    const q = new THREE.Quaternion().setFromEuler(e).normalize();
                    o.multiply(q);
                } else if (o.isVector3) {
                    o.add(e);
                }
            }
        }
    }

    init() {
        // Clock
        this.clock = new THREE.Clock();

        // Scene
        this.scene = new THREE.Scene();

        // Set transparent background
        this.scene.background = null;

        // Camera
        this.camera = new THREE.PerspectiveCamera(
            45,
            window.innerWidth / window.innerHeight,
            0.1,
            1000
        );
        this.camera.position.set(
            29.3 + this.opt.cameraX,
            1.5 + this.opt.cameraY,
            9.7 + this.opt.cameraDistance
        );

        // Apply camera rotation if specified
        if (this.opt.cameraRotateX !== 0 || this.opt.cameraRotateY !== 0) {
            this.camera.rotation.x += this.opt.cameraRotateX;
            this.camera.rotation.y += this.opt.cameraRotateY;
        }

        // Renderer
        this.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true // Enable transparency
        });
        this.renderer.setClearColor(0x000000, 0); // Set clear color to transparent
        this.renderer.setPixelRatio(this.opt.modelPixelRatio * window.devicePixelRatio);
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        // Use outputColorSpace instead of deprecated outputEncoding
        this.renderer.outputColorSpace = THREE.SRGBColorSpace;
        document.getElementById('avatar-container').appendChild(this.renderer.domElement);

        // Controls
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enabled = false; // disables rotation
        this.controls.enableRotate = false;
        this.controls.enablePan = false;
        this.controls.enableZoom = false;

        // Lighting using opt configuration
        this.lightAmbient = new THREE.AmbientLight(
            new THREE.Color(this.opt.lightAmbientColor),
            this.opt.lightAmbientIntensity
        );
        this.lightDirect = new THREE.DirectionalLight(
            new THREE.Color(this.opt.lightDirectColor),
            this.opt.lightDirectIntensity
        );
        this.lightDirect.position.setFromSphericalCoords(
            2,
            this.opt.lightDirectPhi,
            this.opt.lightDirectTheta
        );

        this.scene.add(this.lightAmbient);
        this.scene.add(this.lightDirect);

        // Add spot light if intensity > 0
        if (this.opt.lightSpotIntensity > 0) {
            this.lightSpot = new THREE.SpotLight(
                new THREE.Color(this.opt.lightSpotColor),
                this.opt.lightSpotIntensity
            );
            this.lightSpot.position.setFromSphericalCoords(
                2,
                this.opt.lightSpotPhi,
                this.opt.lightSpotTheta
            );
            this.lightSpot.angle = this.opt.lightSpotDispersion;
            this.scene.add(this.lightSpot);
        }

        // Load model
        this.loadModel();

        // Handle window resize
        window.addEventListener('resize', () => this.onWindowResize(), false);
    }

    /**
     * Set up per-avatar lighting (HDR env map via .hdr file, Key/Fill/Rim lights, soft shadows)
     * Called after model loads so the environment map can be applied to materials.
     */
    setupAvatarLighting() {
        const lc = this.avatarConfig?.lighting;
        if (!lc) return;

        console.log('💡 Applying per-avatar lighting config');

        // --- Tone mapping ---
        const toneMappingMap = {
            'ACESFilmic': THREE.ACESFilmicToneMapping,
            'Reinhard': THREE.ReinhardToneMapping,
            'Cineon': THREE.CineonToneMapping,
            'Linear': THREE.LinearToneMapping,
            'NoToneMapping': THREE.NoToneMapping
        };
        if (lc.toneMapping) {
            this.renderer.toneMapping = toneMappingMap[lc.toneMapping] || THREE.ACESFilmicToneMapping;
        }
        if (lc.toneMappingExposure != null) {
            this.renderer.toneMappingExposure = lc.toneMappingExposure;
        }

        // --- Remove default lights when using per-avatar lighting ---
        if (this.lightAmbient) { this.scene.remove(this.lightAmbient); this.lightAmbient = null; }
        if (this.lightDirect) { this.scene.remove(this.lightDirect); this.lightDirect = null; }
        if (this.lightSpot) { this.scene.remove(this.lightSpot); this.lightSpot = null; }

        // --- Load HDR environment map from .hdr file ---
        if (lc.hdrEnvMap?.enabled && lc.hdrEnvMap?.path) {
            const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
            pmremGenerator.compileEquirectangularShader();
            const envIntensity = lc.hdrEnvMap.intensity ?? 1.0;

            new RGBELoader()
                .load(lc.hdrEnvMap.path, (hdrTexture) => {
                    const envMap = pmremGenerator.fromEquirectangular(hdrTexture).texture;

                    // Use HDR for lighting only (no visible background for avatar)
                    this.scene.environment = envMap;

                    hdrTexture.dispose();
                    pmremGenerator.dispose();

                    // Apply envMapIntensity to all model materials
                    if (this.character) {
                        this.character.traverse(child => {
                            if (child.isMesh && child.material) {
                                const mats = Array.isArray(child.material) ? child.material : [child.material];
                                mats.forEach(mat => {
                                    mat.envMapIntensity = envIntensity;
                                    mat.needsUpdate = true;
                                });
                            }
                        });
                    }
                    console.log('🌐 HDR environment map loaded:', lc.hdrEnvMap.path);
                }, undefined, (error) => {
                    console.error('❌ Failed to load HDR environment map:', error);
                });
        }

        // --- Ambient light (low fill for GI approximation) ---
        if (lc.ambient) {
            this.lightAmbient = new THREE.AmbientLight(
                new THREE.Color(lc.ambient.color || '#ffffff'),
                lc.ambient.intensity ?? 0.6
            );
            this.scene.add(this.lightAmbient);
        }

        // --- Key light (main, with soft shadows) ---
        if (lc.keyLight) {
            const kl = lc.keyLight;
            this.lightKey = new THREE.DirectionalLight(
                new THREE.Color(kl.color || '#ffffff'),
                kl.intensity ?? 1.2
            );
            this.lightKey.position.set(kl.position?.x ?? 2, kl.position?.y ?? 4, kl.position?.z ?? 2);
            if (kl.castShadow) {
                this.lightKey.castShadow = true;
                const sz = kl.shadow?.mapSize ?? 2048;
                this.lightKey.shadow.mapSize.set(sz, sz);
                this.lightKey.shadow.radius = kl.shadow?.radius ?? 4;
                this.lightKey.shadow.bias = kl.shadow?.bias ?? -0.0005;
                this.lightKey.shadow.normalBias = kl.shadow?.normalBias ?? 0.02;
                this.lightKey.shadow.camera.near = 0.1;
                this.lightKey.shadow.camera.far = 50;
                this.lightKey.shadow.camera.left = -5;
                this.lightKey.shadow.camera.right = 5;
                this.lightKey.shadow.camera.top = 5;
                this.lightKey.shadow.camera.bottom = -5;
            }
            this.scene.add(this.lightKey);
            console.log('🔑 Key light added');
        }

        // --- Fill light (opposite side, softer) ---
        if (lc.fillLight) {
            const fl = lc.fillLight;
            this.lightFill = new THREE.DirectionalLight(
                new THREE.Color(fl.color || '#ffffff'),
                fl.intensity ?? 0.4
            );
            this.lightFill.position.set(fl.position?.x ?? -2, fl.position?.y ?? 2, fl.position?.z ?? 3);
            this.lightFill.castShadow = fl.castShadow || false;
            this.scene.add(this.lightFill);
            console.log('💡 Fill light added');
        }

        // --- Rim light (backlight for edge separation) ---
        if (lc.rimLight) {
            const rl = lc.rimLight;
            this.lightRim = new THREE.DirectionalLight(
                new THREE.Color(rl.color || '#ffeedd'),
                rl.intensity ?? 3.5
            );
            this.lightRim.position.set(rl.position?.x ?? 0, rl.position?.y ?? 2, rl.position?.z ?? -3);
            this.lightRim.castShadow = rl.castShadow || false;
            this.scene.add(this.lightRim);
            console.log('✨ Rim light added');
        }

        // --- Chin fill light (under-chin brightener to reduce dark shadows) ---
        const cl = this.avatarConfig?.chinFillLight;
        if (cl) {
            this.lightChinFill = new THREE.DirectionalLight(
                new THREE.Color(cl.color || '#ffffff'),
                cl.intensity ?? 0.5
            );
            this.lightChinFill.position.set(cl.position?.x ?? 0, cl.position?.y ?? -1, cl.position?.z ?? 1.5);
            this.lightChinFill.castShadow = false;
            this.scene.add(this.lightChinFill);
            console.log('🔆 Chin fill light added');
        }

        // Enable shadow casting/receiving on model meshes + apply material overrides
        const mo = this.avatarConfig?.materialOverrides;
        if (this.character) {
            this.character.traverse(child => {
                if (child.isMesh) {
                    child.castShadow = true;
                    child.receiveShadow = true;
                    if (mo && child.material && child.material.isMeshStandardMaterial) {
                        if (mo.roughness != null) child.material.roughness = mo.roughness;
                        if (mo.metalness != null) child.material.metalness = mo.metalness;
                        if (mo.envMapIntensity != null) child.material.envMapIntensity = mo.envMapIntensity;
                        child.material.needsUpdate = true;
                    }
                }
            });
            if (mo) console.log('🎨 Material overrides applied:', mo);
        }
    }

    async loadModel() {
        const gltfLoader = new GLTFLoader();

        // Setup DRACO loader for compressed models
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
        gltfLoader.setDRACOLoader(dracoLoader);

        // Use model path from avatar config or default
        const modelPath = this.avatarConfig?.modelPath || '/Models/Lisa_LowSise_latest.glb';
        const fullModelUrl = modelPath.startsWith('http') ? modelPath : `${API_BASE_URL}${modelPath}`;

        console.log('🔄 Loading model:', modelPath);

        // Try to load from IndexedDB first
        try {
            await glbCache.init();
            const cachedData = await glbCache.getModel(fullModelUrl);

            if (cachedData) {
                console.log('✅ Loading model from IndexedDB cache');
                // Load from cached ArrayBuffer
                gltfLoader.parse(cachedData, '', (gltf) => {
                    this.handleLoadedModel(gltf);
                }, (error) => {
                    console.error('❌ Error parsing cached model:', error);
                    // Fallback to network if cache parse fails
                    this.loadModelFromNetwork(gltfLoader, modelPath, fullModelUrl);
                });
                return;
            }
        } catch (err) {
            console.warn('⚠️ IndexedDB unavailable, loading from network:', err);
        }

        // Load from network if not in cache
        this.loadModelFromNetwork(gltfLoader, modelPath, fullModelUrl);
    }

    loadModelFromNetwork(gltfLoader, modelPath, fullModelUrl) {
        console.log('🌐 Loading model from network:', modelPath);

        let retryCount = 0;
        const maxRetries = 3;

        const attemptLoad = () => {
            // Load from Models folder served by avatar server
            gltfLoader.load(
                modelPath,
                async (gltf) => {
                    // Cache the model in IndexedDB for future use
                    try {
                        const response = await fetch(fullModelUrl);
                        const arrayBuffer = await response.arrayBuffer();
                        await glbCache.saveModel(fullModelUrl, arrayBuffer);
                        console.log('💾 Model saved to IndexedDB cache');
                        await glbCache.getCacheSize();
                    } catch (err) {
                        console.warn('⚠️ Failed to cache model in IndexedDB:', err);
                    }

                    this.handleLoadedModel(gltf);
                },
                (progress) => {
                    const percent = (progress.loaded / progress.total * 100).toFixed(0);
                    console.log(`📦 Loading model: ${percent}%`);
                },
                (error) => {
                    console.error(`❌ Error loading model (attempt ${retryCount + 1}/${maxRetries}):`, error);

                    if (retryCount < maxRetries) {
                        retryCount++;
                        console.log(`🔄 Retrying model load in 2 seconds...`);
                        setTimeout(attemptLoad, 2000);
                    } else {
                        console.error('❌ Failed to load model after', maxRetries, 'attempts');
                        this.sendMessageToParent('AVATAR_ERROR', { error: 'Model loading failed after retries' });
                    }
                }
            );
        };

        attemptLoad();
    }

    handleLoadedModel(gltf) {
        this.character = gltf.scene;

        // Use position from avatar config or default
        const pos = this.avatarConfig?.position || { x: 21, y: -16.6, z: 6.5 };
        const scale = this.avatarConfig?.scale || { x: 12.1, y: 12.1, z: 6 };
        const rot = this.avatarConfig?.rotation || { x: 0, y: 1.16937, z: 0 };

        // Position the model
        this.character.position.set(pos.x, pos.y, pos.z);
        this.character.scale.set(scale.x, scale.y, scale.z);
        this.character.rotation.set(rot.x, rot.y, rot.z);

        // Add to scene and expose for debugging (from Experience 21.js)
        this.scene.add(this.character);
        window.character = this.character;
        window.scene = this.scene;

        // Apply per-avatar camera position if defined in avatars.json
        const camConfig = this.avatarConfig?.camera;
        if (camConfig) {
            this.changeCameraPosition(camConfig);
        }

        // Find morph targets
        this.character.traverse(x => {
            if (x.morphTargetInfluences && x.morphTargetInfluences.length && x.morphTargetDictionary) {
                this.morphs.push(x);
            }
        });

        if (this.morphs.length > 0 && this.mood && this.mood.baseline) {
            const morphKeys = Object.keys(this.morphs[0].morphTargetDictionary);
            morphKeys.forEach(mt => {
                this.setBaselineValue(mt, this.mood.baseline.hasOwnProperty(mt) ? this.mood.baseline[mt] : 0);
            });
        }

        // Initialize pose avatar system (from Experience 21.js)
        this.armature = this.character;
        this.poseAvatar = { props: {} };
        this.posePropNames.forEach(x => {
            const ids = x.split('.');
            const o = this.armature.getObjectByName(ids[0]);
            if (this.poseAvatar && o && ids) {
                this.poseAvatar.props[x] = o[ids[1]];
                if (this.poseBase.props.hasOwnProperty(x)) {
                    this.poseAvatar.props[x].copy(this.poseBase.props[x]);
                } else {
                    this.poseBase.props[x] = this.poseAvatar.props[x].clone();
                }

                // Make sure the target has the delta properties (from Experience 21.js)
                if (this.poseDelta.props.hasOwnProperty(x) && !this.poseTarget.props.hasOwnProperty(x)) {
                    this.poseTarget.props[x] = this.poseAvatar.props[x].clone();
                }

                // Take target pose
                this.poseTarget.props[x].t = this.animClock;
                this.poseTarget.props[x].d = 2000;
            }
        });

        // Reset IK bone positions (from Experience 21.js)
        this.ikMesh.traverse(x => {
            if (x && x.isBone && this.armature.getObjectByName(x.name)) {
                x.position.copy(this.armature.getObjectByName(x.name).position);
            }
        });

        // Setup animations
        this.animations = gltf.animations;
        if (this.animations && this.animations.length) {
            console.log(`📽️ Model has ${this.animations.length} animations:`);
            this.animations.forEach((anim, index) => {
                console.log(`   [${index}] ${anim.name || 'Unnamed'} - Duration: ${anim.duration.toFixed(2)}s`);
            });

            this.mixer = new THREE.AnimationMixer(this.character);
            const idleIndex = this.avatarConfig?.animations?.idle || 0;
            const idleClip = this.animations[idleIndex];

            if (idleClip) {
                const action = this.mixer.clipAction(idleClip);
                const idleTimeScale = this.avatarConfig?.animations?.idleTimeScale || 0.9;
                action.timeScale = idleTimeScale;
                action.play();
                this.activeIdleAction = action;
                this.currentIdleAnimIndex = idleIndex;
                console.log(`✅ Playing idle animation [${idleIndex}]: ${idleClip.name || 'Unnamed'}`);

                // Start idle pool switching if configured
                this.startIdlePoolSwitching();
            } else {
                console.error(`❌ Idle animation index ${idleIndex} not found! Available: 0-${this.animations.length - 1}`);
                console.error(`💡 Update avatars.json: "idle": should be between 0 and ${this.animations.length - 1}`);
                // Play first animation as fallback
                if (this.animations[0]) {
                    const action = this.mixer.clipAction(this.animations[0]);
                    action.timeScale = 0.9;
                    action.play();
                    console.log(`⚠️ Using fallback animation [0]: ${this.animations[0].name || 'Unnamed'}`);
                }
            }
        } else {
            console.warn('⚠️ Model has no animations');
        }

        // Initialize mood system
        if (this.morphs.length > 0) {
            this.setMood(this.opt.avatarMood || 'neutral');
            this.animTimeLast = performance.now();
        }

        // Start idle animations (blink, smile, etc.)
        this.startIdleAnimations();

        // Apply per-avatar lighting (HDR env map, key/fill/rim, soft shadows)
        this.setupAvatarLighting();

        // Notify parent that avatar is ready
        this.sendMessageToParent('AVATAR_READY');
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    /**
     * Start idle pool animation switching (randomly cycles through idlePool animations)
     */
    startIdlePoolSwitching() {
        const idlePool = this.avatarConfig?.animations?.idlePool;
        if (!idlePool || !Array.isArray(idlePool) || idlePool.length === 0 || !this.animations || !this.mixer) {
            return; // No idle pool configured, stays on single idle animation
        }

        const idleTimeScale = this.avatarConfig?.animations?.idleTimeScale || 0.9;
        const crossFadeDur = this.avatarConfig?.animations?.crossFadeDuration ?? 0.8;

        // Build the pool
        const pool = [];
        idlePool.forEach(entry => {
            if (this.animations[entry.index]) {
                pool.push({ clip: this.animations[entry.index], weight: entry.weight || 0.5, index: entry.index, playTime: entry.playTime || 8 });
            }
        });
        if (pool.length === 0) return;

        console.log(`🎭 Idle pool: ${pool.length} animations`);
        pool.forEach(a => console.log(`   [${a.index}] weight=${a.weight}`));

        // Track which animations have been played in the current cycle
        let remainingInCycle = [];

        // Weighted random pick — each index plays only once per cycle, then reshuffles
        const pickRandom = () => {
            // If all animations have been played, start a new cycle
            if (remainingInCycle.length === 0) {
                remainingInCycle = pool.map(a => a.index);
                console.log('🔄 Idle pool cycle reset — all animations available again');
            }
            // Remove current animation from candidates to avoid back-to-back repeat
            let candidates = pool.filter(a => remainingInCycle.includes(a.index) && a.index !== this.currentIdleAnimIndex);
            if (candidates.length === 0) candidates = pool.filter(a => remainingInCycle.includes(a.index));
            if (candidates.length === 0) { remainingInCycle = pool.map(a => a.index); candidates = pool; }

            const totalWeight = candidates.reduce((sum, a) => sum + a.weight, 0);
            let rand = Math.random() * totalWeight;
            for (const entry of candidates) {
                rand -= entry.weight;
                if (rand <= 0) {
                    remainingInCycle = remainingInCycle.filter(i => i !== entry.index);
                    return entry;
                }
            }
            const last = candidates[candidates.length - 1];
            remainingInCycle = remainingInCycle.filter(i => i !== last.index);
            return last;
        };

        const scheduleNext = () => {
            if (this.isSpeaking) return; // Stop switching when talking starts

            const picked = pickRandom();
            if (picked.index === this.currentIdleAnimIndex) {
                // Safety: skip if same animation was picked despite filtering
                const retryDelay = (picked.playTime || 8) * 1000;
                this.idlePoolTimeout = setTimeout(scheduleNext, retryDelay);
                return;
            }
            const newAction = this.mixer.clipAction(picked.clip);
            newAction.setLoop(THREE.LoopRepeat, Infinity);
            newAction.timeScale = idleTimeScale;

            // Use crossFadeTo for seamless transition (prevents T-pose weight gap)
            if (this.activeIdleAction && this.activeIdleAction !== newAction) {
                // Clear any stale fade interpolant from talking pool
                newAction.stop();
                newAction.reset();
                newAction.setEffectiveWeight(1);
                newAction.play();
                this.activeIdleAction.crossFadeTo(newAction, crossFadeDur, true);
            } else if (!newAction.isRunning()) {
                newAction.stop();
                newAction.reset();
                newAction.setEffectiveWeight(1);
                newAction.play();
            }

            this.activeIdleAction = newAction;
            this.currentIdleAnimIndex = picked.index;
            console.log(`🎭 Idle animation [${picked.index}] (timeScale=${idleTimeScale})`);

            // Use per-animation playTime (seconds) from config
            const nextDelay = (picked.playTime || 8) * 1000;
            this.idlePoolTimeout = setTimeout(scheduleNext, nextDelay);
        };

        // Start first switch after the initial idle's playTime
        const initialEntry = pool.find(a => a.index === this.currentIdleAnimIndex);
        const firstDelay = (initialEntry ? initialEntry.playTime : 8) * 1000;
        this.idlePoolTimeout = setTimeout(scheduleNext, firstDelay);
    }

    /**
     * Stop idle pool animation switching
     */
    stopIdlePoolSwitching() {
        if (this.idlePoolTimeout) {
            clearTimeout(this.idlePoolTimeout);
            this.idlePoolTimeout = null;
        }
    }

    /**
     * Transition to idle (pool or single) after speech ends — avoids T-pose
     */
    transitionToIdle() {
        if (!this.animations || this.animations.length === 0 || !this.mixer) return;

        const crossFadeDur = this.avatarConfig?.animations?.crossFadeDuration ?? 0.8;
        const idleIndex = this.avatarConfig?.animations?.idle || 0;
        const idleClip = this.animations[idleIndex];
        const idleTimeScale = this.avatarConfig?.animations?.idleTimeScale || 0.9;

        if (idleClip) {
            const idleAction = this.mixer.clipAction(idleClip);

            // Fade out only non-idle actions to prevent weight gap causing T-pose
            this.mixer._actions.forEach(action => {
                if (action !== idleAction && action.isRunning()) {
                    action.fadeOut(crossFadeDur);
                }
            });

            // CRITICAL: clear any stale fade interpolant from talking pool crossFades
            // Without this, a previous crossFadeTo that faded this action out
            // will override setEffectiveWeight(1) on the next mixer update → T-pose
            idleAction.stop();
            idleAction.reset();
            idleAction.setLoop(THREE.LoopRepeat, Infinity);
            idleAction.timeScale = idleTimeScale;
            idleAction.setEffectiveWeight(1);
            idleAction.play();

            this.activeIdleAction = idleAction;
            this.currentIdleAnimIndex = idleIndex;
        }

        // Start idle pool switching
        this.startIdlePoolSwitching();
    }

    // Start idle animations like blinking and smiling
    startIdleAnimations() {
        this.animTimeLast = performance.now();

        if (!this.mood || !this.mood.anims) return;

        // Queue the blink animation
        const blinkAnim = this.mood.anims.find(a => a.name === 'blink');
        if (blinkAnim) {
            // this.scheduleBlinkAnimation(blinkAnim);
        }

        // Queue the smile/mouth animation
        const mouthAnim = this.mood.anims.find(a => a.name === 'mouth');
        if (mouthAnim) {
            // this.scheduleSmileAnimation(mouthAnim);
        }
    }

    // scheduleBlinkAnimation(animTemplate) {
    //     const delay = this.randomRange(animTemplate.delay[0], animTemplate.delay[1]);

    //     setTimeout(() => {
    //         if (this.morphs.length > 0) {
    //             // Get blink morph targets from config
    //             const blinkLeft = this.avatarConfig?.morphTargets?.blink?.left || 'Eye_Blink_L.001';
    //             const blinkRight = this.avatarConfig?.morphTargets?.blink?.right || 'Eye_Blink_R.001';

    //             // Blink sequence: 0 -> 1 -> 1 -> 0 (from Experience.js)

    //             // Close eyes
    //             this.animateValue(blinkLeft, 0, 1, 50);
    //             this.animateValue(blinkRight, 0, 1, 50);

    //             // Keep closed
    //             setTimeout(() => {
    //                 // Open eyes
    //                 this.animateValue(blinkLeft, 1, 0, 100);
    //                 this.animateValue(blinkRight, 1, 0, 100);
    //             }, this.randomRange(100, 300));
    //         }

    //         // Schedule next blink
    //         this.scheduleBlinkAnimation(animTemplate);
    //     }, delay);
    // }

    // scheduleSmileAnimation(animTemplate) {
    //     const delay = this.randomRange(animTemplate.delay[0], animTemplate.delay[1]);

    //     setTimeout(() => {
    //         // Only animate smile if not speaking
    //         if (!this.isSpeaking) {
    //             const targetSmile = this.randomRange(0.2, 0.8);
    //             const smileMorph = this.avatarConfig?.morphTargets?.smile || 'smile02.001';

    //             this.animateValue(smileMorph, this.getValue(smileMorph), targetSmile, 500);

    //             setTimeout(() => {
    //                 // Check again before animating back
    //                 if (!this.isSpeaking) {
    //                     this.animateValue(smileMorph, targetSmile, 0.2, 500);
    //                 } else {
    //                     // If speaking started, reset smile immediately
    //                     this.setValue(smileMorph, 0);
    //                 }
    //             }, this.randomRange(1000, 5000));
    //         }

    //         // Schedule next smile check
    //         this.scheduleSmileAnimation(animTemplate);
    //     }, delay);
    // }

    randomRange(min, max) {
        return Math.random() * (max - min) + min;
    }

    animateValue(morphTarget, from, to, duration) {
        const startTime = Date.now();
        const smileMorphTarget = this.avatarConfig?.morphTargets?.smile;
        const isSmileAnimation = (morphTarget === smileMorphTarget ||
            morphTarget === 'smile02.001' ||
            morphTarget === 'Smile.001');

        const animate = () => {
            // Stop smile animation if speech starts
            if (isSmileAnimation && this.isSpeaking) {
                this.setValue(morphTarget, 0);
                return;
            }

            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const value = from + (to - from) * this.easing(progress);

            this.setValue(morphTarget, value);

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };
        animate();
    }

    easing(t) {
        // Smooth easing function (from Experience.js)
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }

    setupMessageListener() {
        // Track last received message to prevent duplicates
        let lastMessageText = null;
        let lastMessageTime = 0;
        const DUPLICATE_WINDOW_MS = 1000; // Ignore duplicates within 1 second

        window.addEventListener('message', (event) => {
            const { type, data } = event.data;

            switch (type) {
                case 'SPEAK':
                    if (data && data.text) {
                        // Check if this is a duplicate message received within the time window
                        const now = Date.now();
                        const isDuplicate = (
                            data.text === lastMessageText &&
                            (now - lastMessageTime) < DUPLICATE_WINDOW_MS
                        );

                        if (isDuplicate) {
                            console.warn('⚠️ Duplicate SPEAK message ignored (received within 1s):', data.text.substring(0, 50) + '...');
                            return; // Ignore duplicate
                        }

                        // Update tracking variables
                        lastMessageText = data.text;
                        lastMessageTime = now;

                        this.speakText(data.text);
                    }
                    break;

                case 'STOP':
                    this.stopSpeaking();
                    break;

                case 'CHANGE_BACKGROUND':
                    if (data && data.imageUrl) {
                        this.changeBackground(data.imageUrl);
                    }
                    break;

                case 'RESET_BACKGROUND':
                    this.resetBackground();
                    break;

                case 'CHANGE_CAMERA_POSITION':
                    if (data && data.camera) {
                        this.changeCameraPosition(data.camera);
                    }
                    break;

                case 'SWITCH_AVATAR':
                    if (data && (data.avatar || data.avatarName)) {
                        this.switchAvatar(data.avatar || data.avatarName);
                    }
                    break;

                case 'UPDATE_TRANSFORM':
                    if (data && data.rotation && data.scale) {
                        this.updateAvatarTransform(data.rotation, data.position, data.scale);
                    }
                    break;

                case 'UPDATE_LIGHTING':
                    if (data) {
                        this.updateLighting(data);
                    }
                    break;

                // case 'MOVE_TO_CHAT':
                //     this.moveAvatarToChat();
                //     break;

                // case 'MOVE_TO_DEFAULT':
                //     this.moveAvatarToDefault();
                //     break;
            }
        });
    }

    changeCameraPosition(cameraSettings) {
        if (!this.camera) return;

        // Smoothly transition camera to new position
        const duration = 800; // 800ms transition
        const startPos = {
            x: this.camera.position.x,
            y: this.camera.position.y,
            z: this.camera.position.z
        };
        const endPos = {
            x: cameraSettings.x,
            y: cameraSettings.y,
            z: cameraSettings.z
        };

        const startTime = Date.now();

        const animateCamera = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Smooth easing function
            const easeProgress = progress < 0.5
                ? 2 * progress * progress
                : -1 + (4 - 2 * progress) * progress;

            this.camera.position.x = startPos.x + (endPos.x - startPos.x) * easeProgress;
            this.camera.position.y = startPos.y + (endPos.y - startPos.y) * easeProgress;
            this.camera.position.z = startPos.z + (endPos.z - startPos.z) * easeProgress;

            if (progress < 1) {
                requestAnimationFrame(animateCamera);
            }
        };

        animateCamera();
    }

    changeBackground(imageUrl) {
        const avatarContainer = document.getElementById('avatar-container');
        if (avatarContainer) {
            avatarContainer.style.backgroundImage = `url(${imageUrl})`;
            avatarContainer.style.backgroundSize = 'cover';
            avatarContainer.style.backgroundPosition = 'center';
            avatarContainer.style.backgroundRepeat = 'no-repeat';
            console.log('✅ Background image applied to avatar-container');
        }
        // Keep Three.js scene transparent so background shows through
        this.scene.background = null;
    }

    resetBackground() {
        const avatarContainer = document.getElementById('avatar-container');
        if (avatarContainer) {
            avatarContainer.style.backgroundImage = '';
            console.log('✅ Background reset to transparent');
        }
        // Reset to transparent background
        this.scene.background = null;
    }

    updateAvatarTransform(rotation, position, scale) {
        if (!this.character) {
            console.warn('⚠️ Character not loaded yet');
            return;
        }

        // Update the character rotation
        this.character.rotation.set(rotation.x, rotation.y, rotation.z);

        // Update the character position if provided
        if (position) {
            this.character.position.set(position.x, position.y, position.z);
        }

        // Update the character scale
        this.character.scale.set(scale.x, scale.y, scale.z);

        console.log(`🔄 Avatar transform updated:`);
        console.log(`   Rotation: x=${rotation.x.toFixed(2)}, y=${rotation.y.toFixed(2)}, z=${rotation.z.toFixed(2)}`);
        if (position) {
            console.log(`   Position: x=${position.x.toFixed(2)}, y=${position.y.toFixed(2)}, z=${position.z.toFixed(2)}`);
        }
        console.log(`   Scale: x=${scale.x.toFixed(2)}, y=${scale.y.toFixed(2)}, z=${scale.z.toFixed(2)}`);
    }

    updateLighting(data) {
        // Key light
        if (this.lightKey && data.keyLight) {
            const kl = data.keyLight;
            if (kl.intensity != null) this.lightKey.intensity = kl.intensity;
            if (kl.color) this.lightKey.color.set(kl.color);
            if (kl.position) this.lightKey.position.set(kl.position.x, kl.position.y, kl.position.z);
        }
        // Fill light
        if (this.lightFill && data.fillLight) {
            const fl = data.fillLight;
            if (fl.intensity != null) this.lightFill.intensity = fl.intensity;
            if (fl.color) this.lightFill.color.set(fl.color);
            if (fl.position) this.lightFill.position.set(fl.position.x, fl.position.y, fl.position.z);
        }
        // Rim light
        if (this.lightRim && data.rimLight) {
            const rl = data.rimLight;
            if (rl.intensity != null) this.lightRim.intensity = rl.intensity;
            if (rl.color) this.lightRim.color.set(rl.color);
            if (rl.position) this.lightRim.position.set(rl.position.x, rl.position.y, rl.position.z);
        }
        // Ambient light
        if (this.lightAmbient && data.ambient) {
            if (data.ambient.intensity != null) this.lightAmbient.intensity = data.ambient.intensity;
            if (data.ambient.color) this.lightAmbient.color.set(data.ambient.color);
        }
        // Chin fill light
        if (this.lightChinFill && data.chinFillLight) {
            const cl = data.chinFillLight;
            if (cl.intensity != null) this.lightChinFill.intensity = cl.intensity;
            if (cl.color) this.lightChinFill.color.set(cl.color);
            if (cl.position) this.lightChinFill.position.set(cl.position.x, cl.position.y, cl.position.z);
        }
        // Tone mapping exposure
        if (this.renderer && data.toneMappingExposure != null) {
            this.renderer.toneMappingExposure = data.toneMappingExposure;
        }
        // HDR env map intensity
        if (data.hdrEnvMapIntensity != null && this.character) {
            this.character.traverse(child => {
                if (child.isMesh && child.material) {
                    child.material.envMapIntensity = data.hdrEnvMapIntensity;
                    child.material.needsUpdate = true;
                }
            });
        }
        // Material overrides
        if (data.materialOverrides && this.character) {
            const mo = data.materialOverrides;
            this.character.traverse(child => {
                if (child.isMesh && child.material && child.material.isMeshStandardMaterial) {
                    if (mo.roughness != null) child.material.roughness = mo.roughness;
                    if (mo.metalness != null) child.material.metalness = mo.metalness;
                    if (mo.envMapIntensity != null) child.material.envMapIntensity = mo.envMapIntensity;
                    child.material.needsUpdate = true;
                }
            });
        }
    }

    speakText(text) {
        if (!text || typeof text !== 'string' || text.trim() === '') {
            console.warn('Invalid text provided to speakText');
            return;
        }

        // Check if this exact text is already in the queue or currently being processed
        const isDuplicate = this.speechQueue.includes(text) ||
            (this.isSpeaking && this.currentSpeechText === text);

        if (isDuplicate) {
            return;
        }

        // Add text to speech queue
        this.speechQueue.push(text);

        // Start processing queue if not already processing
        if (!this.isProcessingQueue) {
            this.processNextInQueue();
        }
    }

    processNextInQueue() {
        // If queue is empty or already speaking, return
        if (this.speechQueue.length === 0) {
            this.isProcessingQueue = false;

            // Ensure stop button is disabled when queue is empty and not speaking
            // if (!this.isSpeaking && window.resetIconToDefault) {
            //     window.resetIconToDefault();
            // }
            return;
        }

        if (this.isSpeaking) {
            return;
        }

        this.isProcessingQueue = true;
        const text = this.speechQueue.shift(); // Get next item from queue

        this.processSpeech(text);
    }

    processSpeech(text) {
        this.isSpeaking = true;
        this.currentSpeechText = text; // Track current speech text

        // Stop idle pool switching when speech starts
        this.stopIdlePoolSwitching();

        // Immediately reset smile morph target to 0 when speech starts
        const smileMorphTarget = this.avatarConfig?.morphTargets?.smile;
        if (smileMorphTarget) {
            this.setValue(smileMorphTarget, 0);
        }

        // Enable stop button when speaking starts
        // if (window.enableStopButton) {
        //     window.enableStopButton();
        // }

        // Prepare request body for TTS and blend data
        const requestBody = {
            text: text,
            avatar: this.avatarName,
            clientSessionId: CLIENT_SESSION_ID
        };

        // Fetch from server using centralized API client with cache control
        apiClient.post(`${API_BASE_URL}/talk`, requestBody)
            .then(response => {
                if (!response.ok) {
                    // Try to parse error details from server
                    return response.json().then(errData => {
                        console.error('❌ Server error response:', errData);
                        const errorMsg = errData.details || errData.error || 'Unknown server error';
                        throw new Error(`HTTP error! status: ${response.status} - ${errorMsg}`);
                    }).catch(parseError => {
                        // If JSON parsing fails, throw basic error
                        if (parseError.message.includes('HTTP error!')) {
                            throw parseError;
                        }
                        throw new Error(`HTTP error! status: ${response.status}`);
                    });
                }
                return response.json();
            })
            .then(blend => {
                if (!blend || !blend.filename || !blend.blendData) {
                    throw new Error('Invalid TTS response data');
                }

                // Collect all morph target objects for multi-mesh support (e.g., body + teeth)
                const morphTargetObjects = [];

                // Get primary morph target object
                const primaryMorphTarget = this.character.getObjectByName(
                    this.avatarConfig?.morphTargetObject || "CC_Base_Body_1"
                );
                if (primaryMorphTarget && primaryMorphTarget.morphTargetDictionary) {
                    morphTargetObjects.push(primaryMorphTarget);
                }

                // Find all other objects with morph targets (e.g., teeth, tongue)
                this.character.traverse(obj => {
                    if (obj.morphTargetDictionary &&
                        obj.morphTargetInfluences &&
                        !morphTargetObjects.includes(obj)) {
                        morphTargetObjects.push(obj);
                    }
                });

                if (morphTargetObjects.length === 0) {
                    console.error('❌ No morph target objects found in character');
                    return;
                }

                console.log(`✅ Found ${morphTargetObjects.length} morph target object(s) for visemes`);

                // Helper function to apply viseme across all morph target objects
                const applyViseme = (viseme, targetValue, factor) => {
                    let applied = false;
                    for (const morphtarget of morphTargetObjects) {
                        const morphIndex = morphtarget.morphTargetDictionary[viseme];
                        if (morphIndex !== undefined) {
                            morphtarget.morphTargetInfluences[morphIndex] = interpolate(
                                morphtarget.morphTargetInfluences[morphIndex],
                                targetValue,
                                factor
                            );
                            applied = true;
                        }
                    }
                    return applied;
                };

                const audio = new Audio(`${API_BASE_URL}/voice${blend.filename}`);
                audio.muted = this.opt.avatarMute; // Apply mute option

                this.activeSpeechAudios.push(audio);
                this.currentSpeechAudio = audio;
                this.currentBlendData = blend.blendData;

                let lastUpdateTime = 0;
                const updateInterval = 50;
                let visemeApplyCount = 0;

                // Per-avatar viseme intensity (configurable in avatars.json)
                const visemeIntensity = this.avatarConfig?.visemeIntensity ?? 0.8;

                const interpolate = (value, target, factor) => {
                    return value + (target - value) * factor;
                };

                // Update morph targets based on audio playback (from Experience.js)
                audio.ontimeupdate = () => {
                    const now = Date.now();
                    if (now - lastUpdateTime > updateInterval) {
                        lastUpdateTime = now;

                        const currentAudioTime = audio.currentTime;
                        let updated = false;

                        // Apply visemes based on current audio time
                        for (let i = 0; i < blend.blendData.length; i++) {
                            const timeDataStart = blend.blendData[i].start / 1000;
                            const timeDataEnd = blend.blendData[i].end / 1000;
                            const viseme = blend.blendData[i].viseme;

                            if (currentAudioTime >= timeDataStart && currentAudioTime <= timeDataEnd) {
                                // Apply viseme across all morph target objects (body + teeth)
                                const applied = applyViseme(viseme, visemeIntensity, 0.5);
                                if (applied) {
                                    visemeApplyCount++;
                                } else {
                                    console.warn(`Viseme "${viseme}" not found in any morph target object`);
                                }
                                updated = true;
                                break;
                            }
                        }

                        if (!updated) {
                            // Gradually reset all morph targets
                            for (let i = 0; i < blend.blendData.length; i++) {
                                const viseme = blend.blendData[i].viseme;
                                applyViseme(viseme, 0, 0.7);
                            }
                        }
                    }
                };

                // Randomly switch between talking animations while speaking
                let animSwitchTimeout = null;
                let activeAction = null;
                if (this.animations && this.animations.length > 0 && this.mixer) {
                    const crossFadeDuration = this.avatarConfig?.animations?.crossFadeDuration ?? 0.8;
                    const idleIndex = this.avatarConfig?.animations?.idle ?? 0;
                    const idleClip = this.animations[idleIndex];
                    const idleTimeScale = this.avatarConfig?.animations?.idleTimeScale || 0.9;
                    const talkingTimeScale = this.avatarConfig?.animations?.talkingTimeScale || 1.0;
                    const talkingPool = this.avatarConfig?.animations?.talkingPool;
                    const firstTalkingAnim = this.avatarConfig?.animations?.firstTalkingAnimation;

                    // Build weighted animation pool
                    let animPool = [];
                    if (talkingPool && Array.isArray(talkingPool) && talkingPool.length > 0) {
                        talkingPool.forEach(entry => {
                            if (this.animations[entry.index]) {
                                animPool.push({ clip: this.animations[entry.index], weight: entry.weight || 0.5, index: entry.index, playTime: entry.playTime || 6, timeScale: entry.timeScale ?? talkingTimeScale });
                            }
                        });
                    }

                    // Fallback: if no pool configured, use single talking animation
                    if (animPool.length === 0) {
                        const talkingIndex = this.avatarConfig?.animations?.talking ?? 1;
                        const talkingClip = this.animations[talkingIndex] || this.animations[0];
                        if (talkingClip) {
                            animPool.push({ clip: talkingClip, weight: 1.0, index: talkingIndex });
                        }
                    }

                    console.log(`🎬 Animation pool: ${animPool.length} animations, talkingTimeScale=${talkingTimeScale}`);
                    animPool.forEach(a => console.log(`   [${a.index}] weight=${a.weight}`));

                    // Get current idle action reference
                    let idleAction = null;
                    if (idleClip) {
                        idleAction = this.mixer.clipAction(idleClip);
                        idleAction.setLoop(THREE.LoopRepeat, Infinity);
                        idleAction.timeScale = idleTimeScale;
                        if (idleAction.isRunning()) {
                            activeAction = idleAction;
                        }
                    }

                    // Stop all currently running actions to prevent idle poses from blending
                    this.mixer._actions.forEach(action => {
                        if (action.isRunning()) {
                            action.fadeOut(0.8);
                        }
                    });
                    
                    // Weighted random selection from pool
                    const pickRandomFromPool = (excludeIndex) => {
                        // Filter out the currently playing animation to avoid picking the same one
                        let candidates = excludeIndex != null
                            ? animPool.filter(a => a.index !== excludeIndex)
                            : animPool;
                        if (candidates.length === 0) candidates = animPool;

                        const totalWeight = candidates.reduce((sum, a) => sum + a.weight, 0);
                        let rand = Math.random() * totalWeight;
                        for (const entry of candidates) {
                            rand -= entry.weight;
                            if (rand <= 0) return entry;
                        }
                        return candidates[candidates.length - 1];
                    };

                    // Switch to a specific action with crossfade
                    let currentAnimIndex = null;
                    let isFirstTalkingTransition = true;
                    const switchToAction = (newAction, animIndex, entryTimeScale) => {
                        if (!newAction || newAction === activeAction) return;

                        newAction.setLoop(THREE.LoopRepeat, Infinity);
                        const speed = entryTimeScale ?? talkingTimeScale;

                        // Use crossFadeTo for seamless transition (prevents T-pose weight gap)
                        if (activeAction) {
                            if (isFirstTalkingTransition) {
                                // First transition from idle: stop idle immediately to avoid pose blending
                                isFirstTalkingTransition = false;
                                activeAction.fadeOut(0.1);
                                newAction.stop();
                                newAction.reset();
                                newAction.timeScale = speed;
                                newAction.setEffectiveWeight(1);
                                newAction.play();
                            } else {
                                // Talking→talking transitions: use normal crossfade (warp=false to preserve timeScale)
                                newAction.stop();
                                newAction.reset();
                                newAction.timeScale = speed;
                                newAction.setEffectiveWeight(1);
                                newAction.play();
                                activeAction.crossFadeTo(newAction, crossFadeDuration, false);
                            }
                        } else {
                            newAction.stop();
                            newAction.reset();
                            newAction.timeScale = speed;
                            newAction.setEffectiveWeight(1);
                            newAction.play();
                        }

                        activeAction = newAction;
                        currentAnimIndex = animIndex;
                        console.log(`🎬 Playing animation [${animIndex}] (timeScale=${speed})`);
                    };

                    // Schedule random animation switches
                    const scheduleSwitch = () => {
                        if (!this.isSpeaking) return;

                        const picked = pickRandomFromPool(currentAnimIndex);
                        if (picked.index === currentAnimIndex) {
                            // Safety: skip if same animation was picked despite filtering
                            const nextDelay = (picked.playTime || 6) * 1000;
                            animSwitchTimeout = setTimeout(scheduleSwitch, nextDelay);
                            return;
                        }
                        const action = this.mixer.clipAction(picked.clip);
                        action.setLoop(THREE.LoopRepeat, Infinity);
                        switchToAction(action, picked.index, picked.timeScale);

                        // Use per-animation playTime (seconds) from config
                        const nextDelay = (picked.playTime || 6) * 1000;
                        animSwitchTimeout = setTimeout(scheduleSwitch, nextDelay);
                    };

                    // Start with firstTalkingAnimation or animation 1
                    const firstIndex = firstTalkingAnim ?? (this.avatarConfig?.animations?.talking ?? 1);
                    const firstClip = this.animations[firstIndex];
                    if (firstClip) {
                        const firstAction = this.mixer.clipAction(firstClip);
                        firstAction.setLoop(THREE.LoopRepeat, Infinity);
                        const firstPoolEntry2 = animPool.find(a => a.index === firstIndex);
                        switchToAction(firstAction, firstIndex, firstPoolEntry2?.timeScale);
                    }

                    // Start random switching after first animation plays for its configured playTime
                    const firstPoolEntry = animPool.find(a => a.index === firstIndex);
                    const firstPlayTime = firstPoolEntry ? firstPoolEntry.playTime : 6;
                    const firstDuration = firstPlayTime * 1000;
                    animSwitchTimeout = setTimeout(scheduleSwitch, firstDuration);
                }

                audio.onended = () => {
                    // Stop random animation switching
                    if (animSwitchTimeout) {
                        clearTimeout(animSwitchTimeout);
                        animSwitchTimeout = null;
                    }

                    // Gradually reset morph targets across all objects
                    for (let i = 0; i < blend.blendData.length; i++) {
                        const viseme = blend.blendData[i].viseme;
                        applyViseme(viseme, 0, 0.7);
                    }

                    // Return to idle animation — avoid T-pose
                    this.transitionToIdle();

                    this.isSpeaking = false;
                    this.currentSpeechText = null; // Clear current speech text
                    this.sendMessageToParent('SPEECH_ENDED');
                    // Disable stop button and reset icon when speech ends naturally
                    // if (window.resetIconToDefault) {
                    //     window.resetIconToDefault();
                    // }

                    // Process next item in queue after a short delay
                    setTimeout(() => {
                        this.processNextInQueue();
                    }, 300);
                };

                audio.onerror = (e) => {
                    console.error('❌ Audio error:', e);
                    this.isSpeaking = false;
                    this.currentSpeechText = null; // Clear current speech text
                    this.sendMessageToParent('SPEECH_ERROR', { error: 'Audio playback failed' });
                    // Disable stop button and reset icon on error
                    // if (window.resetIconToDefault) {
                    //     window.resetIconToDefault();
                    // }

                    // Process next item in queue even on error
                    setTimeout(() => {
                        this.processNextInQueue();
                    }, 300);
                };

                // Play audio
                audio.play().catch(err => {
                    console.error('❌ Audio play error:', err);
                    this.isSpeaking = false;
                    this.currentSpeechText = null; // Clear current speech text
                    // Reset icon to default on play error
                    // if (window.resetIconToDefault) {
                    //     window.resetIconToDefault();
                    // }
                    // Process next item in queue even on play error
                    setTimeout(() => {
                        this.processNextInQueue();
                    }, 300);
                });
                this.sendMessageToParent('SPEECH_STARTED');
            })
            .catch(error => {
                console.error('❌ Error in processSpeech:', error);
                this.isSpeaking = false;
                this.currentSpeechText = null; // Clear current speech text
                this.sendMessageToParent('SPEECH_ERROR', { error: error.message });
                // Disable stop button and reset icon on fetch error
                // if (window.resetIconToDefault) {
                //     window.resetIconToDefault();
                // }
                // Process next item in queue even on fetch error
                setTimeout(() => {
                    this.processNextInQueue();
                }, 300);
            });
    }

    stopSpeaking() {
        // Stop current speech only, keep queue intact for remaining questions
        this.currentSpeechText = null; // Clear current speech text

        // Stop all active audio
        if (this.activeSpeechAudios.length > 0) {
            this.activeSpeechAudios.forEach(audio => {
                if (!audio.paused) {
                    audio.pause();
                    audio.currentTime = 0;
                    audio.ontimeupdate = null;
                    audio.onended = null; // Remove onended to prevent queue processing
                }
            });
            this.activeSpeechAudios = [];
        }

        // Reset morph targets
        if (this.currentBlendData) {
            this.resetMorphTargets(null, this.currentBlendData);
        }

        // Return to idle — fade transition (avoids T-pose)
        this.transitionToIdle();

        this.isSpeaking = false;

        // Disable stop button when speech is stopped
        // if (window.resetIconToDefault) {
        //     window.resetIconToDefault();
        // }

        this.sendMessageToParent('SPEECH_STOPPED');

        // Process next question in queue after a short delay
        setTimeout(() => {
            this.processNextInQueue();
        }, 500);
    }

    resetMorphTargets(morphtarget, blendData) {
        if (!blendData) return;

        // Collect all morph target objects
        const morphTargetObjects = [];
        this.character.traverse(obj => {
            if (obj.morphTargetDictionary && obj.morphTargetInfluences) {
                morphTargetObjects.push(obj);
            }
        });

        // Reset visemes across all morph target objects
        for (let i = 0; i < blendData.length; i++) {
            const viseme = blendData[i].viseme;
            for (const morphObj of morphTargetObjects) {
                const morphIndex = morphObj.morphTargetDictionary[viseme];
                if (morphIndex !== undefined) {
                    morphObj.morphTargetInfluences[morphIndex] = 0;
                }
            }
        }
    }

    moveAvatarToChat() {
        if (this.character) {
            this.character.position.set(26, -4.2, 8);
            this.character.scale.set(4, 4, 4);
            this.character.rotation.set(0, 1.5, 0);
        }
    }

    moveAvatarToDefault() {
        if (this.character) {
            this.character.position.set(21, -8.5, 6.5);
            this.character.scale.set(6, 6, 6);
            this.character.rotation.set(0, 1.16937, 0);
        }
    }

    sendMessageToParent(type, data = {}) {
        // Send message to parent window (chatbot app)
        window.parent.postMessage({
            type: type,
            data: data
        }, '*'); // Replace '*' with specific origin in production
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    // ==================== ANIMATION SYSTEM FUNCTIONS (from Experience 21.js) ====================

    /**
     * Get morph target value (from Experience 21.js)
     * @param {string} mt Morph target name
     * @return {number} Current value
     */
    getValue(mt) {
        if (mt === 'headRotateX') {
            return this.poseDelta?.props['Head.quaternion']?.x || 0;
        } else if (mt === 'headRotateY') {
            return this.poseDelta?.props['Head.quaternion']?.y || 0;
        } else if (mt === 'headRotateZ') {
            return this.poseDelta?.props['Head.quaternion']?.z || 0;
        } else if (mt.startsWith('handFist')) {
            const side = mt.substring(8);
            return this.poseDelta?.props[side + 'HandMiddle1.quaternion']?.x || 0;
        } else if (mt === 'chestInhale') {
            return (this.poseDelta?.props['Spine1.scale']?.x || 0) * 20;
        } else {
            if (this.morphs.length === 0) return 0;
            const ndx = this.morphs[0].morphTargetDictionary[mt];
            if (ndx !== undefined) {
                return this.morphs[0].morphTargetInfluences[ndx];
            }
            return 0;
        }
    }

    /**
     * Set morph target value (from Experience 21.js)
     * @param {string} mt Morph target name
     * @param {number} v Value
     */
    setValue(mt, v) {
        // Disable smile when speaking - check against configured smile morph target
        const smileMorphTarget = this.avatarConfig?.morphTargets?.smile;
        if (this.isSpeaking && (
            mt === smileMorphTarget ||
            mt === this.mapping.mouthSmile ||
            mt === "ShapeKey_Smile" ||
            mt === "smile02.001" ||
            mt === "Smile.001"
        )) {
            v = 0;
        }

        // Handle pose delta rotations (from Experience 21.js)
        if (mt === 'headRotateX') {
            if (this.poseDelta?.props) {
                this.poseDelta.props['Head.quaternion'].x = v;
                this.poseDelta.props['Spine1.quaternion'].x = v / 2;
                this.poseDelta.props['Spine.quaternion'].x = v / 8;
                this.poseDelta.props['Hips.quaternion'].x = v / 24;
            }
        } else if (mt === 'headRotateY') {
            if (this.poseDelta?.props) {
                this.poseDelta.props['Head.quaternion'].y = v;
                this.poseDelta.props['Spine1.quaternion'].y = v / 2;
                this.poseDelta.props['Spine.quaternion'].y = v / 2;
                this.poseDelta.props['Hips.quaternion'].y = v / 4;
                this.poseDelta.props['LeftUpLeg.quaternion'].y = v / 2;
                this.poseDelta.props['RightUpLeg.quaternion'].y = v / 2;
                this.poseDelta.props['LeftLeg.quaternion'].y = v / 4;
                this.poseDelta.props['RightLeg.quaternion'].y = v / 4;
            }
        } else if (mt === 'headRotateZ') {
            if (this.poseDelta?.props) {
                this.poseDelta.props['Head.quaternion'].z = v;
                this.poseDelta.props['Spine1.quaternion'].z = v / 12;
                this.poseDelta.props['Spine.quaternion'].z = v / 12;
                this.poseDelta.props['Hips.quaternion'].z = v / 24;
            }
        } else if (mt.startsWith('handFist')) {
            if (this.poseDelta?.props) {
                const side = mt.substring(8);
                ['HandThumb', 'HandIndex', 'HandMiddle', 'HandRing', 'HandPinky'].forEach((x, i) => {
                    if (i === 0) {
                        this.poseDelta.props[side + x + '1.quaternion'].x = 0;
                        this.poseDelta.props[side + x + '2.quaternion'].z = (side === 'Left' ? -1 : 1) * v;
                        this.poseDelta.props[side + x + '3.quaternion'].z = (side === 'Left' ? -1 : 1) * v;
                    } else {
                        this.poseDelta.props[side + x + '1.quaternion'].x = v;
                        this.poseDelta.props[side + x + '2.quaternion'].x = 1.5 * v;
                        this.poseDelta.props[side + x + '3.quaternion'].x = 1.5 * v;
                    }
                });
            }
        } else if (mt === 'chestInhale') {
            if (this.poseDelta?.props) {
                const scale = v / 20;
                const d = { x: scale, y: (scale / 2), z: (3 * scale) };
                const dneg = { x: (1 / (1 + scale) - 1), y: (1 / (1 + scale / 2) - 1), z: (1 / (1 + 3 * scale) - 1) };
                this.poseDelta.props['Spine1.scale'] = d;
                this.poseDelta.props['Neck.scale'] = dneg;
                this.poseDelta.props['LeftArm.scale'] = dneg;
                this.poseDelta.props['RightArm.scale'] = dneg;
            }
        } else {
            // Apply to all morph targets
            this.morphs.forEach(x => {
                const ndx = x.morphTargetDictionary[mt];
                if (ndx !== undefined) {
                    x.morphTargetInfluences[ndx] = v;
                }
            });
        }
    }

    /**
     * Get baseline value for morph target (from Experience 21.js)
     * @param {string} mt Morph target name
     * @return {number} Baseline value, undefined if not in baseline
     */
    getBaselineValue(mt) {
        if (mt === 'eyesRotateY') {
            const ll = this.getBaselineValue('eyeLookOutLeft');
            if (ll === undefined) return undefined;
            const lr = this.getBaselineValue('eyeLookInLeft');
            if (lr === undefined) return undefined;
            const rl = this.getBaselineValue('eyeLookOutRight');
            if (rl === undefined) return undefined;
            const rr = this.getBaselineValue('eyeLookInRight');
            if (rr === undefined) return undefined;
            return ll - lr;
        } else if (mt === 'eyesRotateX') {
            const d = this.getBaselineValue('eyesLookDown');
            if (d === undefined) return undefined;
            const u = this.getBaselineValue('eyesLookUp');
            if (u === undefined) return undefined;
            return d - u;
        } else {
            return (this.animBaseline.hasOwnProperty(mt) ? this.animBaseline[mt].target : undefined);
        }
    }

    /**
     * Set baseline for morph target (from Experience 21.js)
     * @param {string} mt Morph target name
     * @param {number} v Value, null if to be removed from baseline
     */
    setBaselineValue(mt, v) {
        if (mt === 'eyesRotateY') {
            this.setBaselineValue('eyeLookOutLeft', (v === null) ? null : (v > 0 ? v : 0));
            this.setBaselineValue('eyeLookInLeft', (v === null) ? null : (v > 0 ? 0 : -v));
            this.setBaselineValue('eyeLookOutRight', (v === null) ? null : (v > 0 ? 0 : -v));
            this.setBaselineValue('eyeLookInRight', (v === null) ? null : (v > 0 ? v : 0));
        } else if (mt === 'eyesRotateX') {
            this.setBaselineValue('eyesLookDown', (v === null) ? null : (v > 0 ? v : 0));
            this.setBaselineValue('eyesLookUp', (v === null) ? null : (v > 0 ? 0 : -v));
        } else if (mt === 'eyeLookOutLeft' || mt === 'eyeLookInLeft' ||
            mt === 'eyeLookOutRight' || mt === 'eyeLookInRight' ||
            mt === 'eyesLookDown' || mt === 'eyesLookUp') {
            // Skip these - they are handled by eyesRotateX/Y
        } else {
            if (v === null) {
                if (this.animBaseline.hasOwnProperty(mt)) {
                    delete this.animBaseline[mt];
                }
            } else {
                this.animBaseline[mt] = { target: v };
            }
        }
    }

    /**
     * Create a sigmoid function for smooth easing
     * @param {number} k - Sharpness of ease
     * @return {function} Sigmoid function
     */
    sigmoidFactory(k) {
        function base(t) { return (1 / (1 + Math.exp(-k * t))) - 0.5; }
        const corr = 0.5 / base(1);
        return function (t) { return corr * base(2 * Math.max(Math.min(t, 1), 0) - 1) + 0.5; };
    }



    /**
     * Get morph target value
     * @param {number} start Start value
     * @param {number} end End value
     * @param {number} skew Skew factor
     * @param {number} samples Number of samples
     * @return {number} Random value
     */
    gaussianRandom(start, end, skew = 1, samples = 5) {
        let r = 0;
        for (let i = 0; i < samples; i++) r += Math.random();
        return start + Math.pow(r / samples, skew) * (end - start);
    }

    /**
     * Calculate value based on time using animation sequence
     * @param {number[]} ts Time sequence
     * @param {number[]} vs Value sequence
     * @param {number} t Current time
     * @param {function} fun Easing function
     * @return {number} Interpolated value
     */
    valueAnimationSeq(ts, vs, t, fun = null) {
        let iMin = 0;
        let iMax = ts.length - 1;
        if (t <= ts[iMin]) return (typeof vs[iMin] === 'function' ? vs[iMin]() : vs[iMin]);
        if (t >= ts[iMax]) return (typeof vs[iMax] === 'function' ? vs[iMax]() : vs[iMax]);
        while (t > ts[iMin + 1]) iMin++;
        iMax = iMin + 1;
        let k = ((typeof vs[iMax] === 'function' ? vs[iMax]() : vs[iMax]) -
            (typeof vs[iMin] === 'function' ? vs[iMin]() : vs[iMin])) / (ts[iMax] - ts[iMin]);
        if (fun) k = fun((t - ts[iMin]) / (ts[iMax] - ts[iMin])) * k;
        const b = (typeof vs[iMin] === 'function' ? vs[iMin]() : vs[iMin]) - (k * ts[iMin]);
        return (k * t + b);
    }

    /**
     * Create animation from template (from Experience 21.js)
     * @param {Object} t Animation template
     * @param {number} loop Number of loops
     * @param {number} scaleTime Time scale
     * @param {number} scaleValue Value scale
     * @return {Object} Animation object
     */
    animFactory(t, loop = false, scaleTime = 1, scaleValue = 1) {
        const o = { template: t, ts: [0], vs: {} };

        // Follow the hierarchy of objects (from Experience 21.js)
        let a = t;
        while (true) {
            if (a.hasOwnProperty(this.stateName)) {
                a = a[this.stateName];
            } else if (a.hasOwnProperty(this.moodName)) {
                a = a[this.moodName];
            } else if (a.hasOwnProperty(this.poseName)) {
                a = a[this.poseName];
            } else if (a.hasOwnProperty('idle')) {
                a = a.idle;
            } else if (a.hasOwnProperty('talking') && this.isSpeaking) {
                a = a.talking;
            } else if (a.hasOwnProperty('alt')) {
                // Handle alternatives with probabilities
                let b = a.alt[0];
                if (a.alt.length > 1) {
                    const coin = Math.random();
                    let p = 0;
                    for (let i = 0; i < a.alt.length; i++) {
                        p += a.alt[i].p || (1 - p) / (a.alt.length - i);
                        if (coin < p) {
                            b = a.alt[i];
                            break;
                        }
                    }
                }
                a = b;
            } else {
                break;
            }
        }

        // Check if animation has required vs property
        if (!a.hasOwnProperty('vs')) {
            console.warn('Animation template missing "vs" property:', t.name);
            return { template: t, ts: [this.animClock], vs: {}, loop: loop };
        }

        // Calculate time sequence
        const delay = a.delay ? (Array.isArray(a.delay) ?
            this.gaussianRandom(a.delay[0], a.delay[1], a.delay[2] || 1, a.delay[3] || 5) : a.delay) : 0;

        if (a.hasOwnProperty('dt')) {
            a.dt.forEach((x, i) => {
                o.ts[i + 1] = o.ts[i] + (Array.isArray(x) ?
                    this.gaussianRandom(x[0], x[1], x[2] || 1, x[3] || 5) : x);
            });
        }
        o.ts = o.ts.map(x => this.animClock + delay + x * scaleTime);

        // Process values (from Experience 21.js)
        for (let [mt, vs] of Object.entries(a.vs)) {
            const base = this.getBaselineValue(mt);
            const v = vs.map(x => {
                if (x === null) return null;
                if (typeof x === 'function') return x;
                if (typeof x === 'string' || x instanceof String) return x.slice();
                if (Array.isArray(x)) {
                    return (base === undefined ? 0 : base) + scaleValue *
                        this.gaussianRandom(x[0], x[1], x[2] || 1, x[3] || 5);
                }
                if (x instanceof Object && x.constructor === Object) {
                    return Object.assign({}, x);
                }
                return (base === undefined ? 0 : base) + scaleValue * x;
            });

            // Handle eye rotation morphs (from Experience 21.js)
            if (mt === 'eyesRotateY') {
                o.vs['eyeLookOutLeft'] = [null, ...v.map(x => (x > 0) ? x : 0)];
                o.vs['eyeLookInLeft'] = [null, ...v.map(x => (x > 0) ? 0 : -x)];
                o.vs['eyeLookOutRight'] = [null, ...v.map(x => (x > 0) ? 0 : -x)];
                o.vs['eyeLookInRight'] = [null, ...v.map(x => (x > 0) ? x : 0)];
            } else if (mt === 'eyesRotateX') {
                o.vs['eyesLookDown'] = [null, ...v.map(x => (x > 0) ? x : 0)];
                o.vs['eyesLookUp'] = [null, ...v.map(x => (x > 0) ? 0 : -x)];
            } else {
                o.vs[mt] = [null, ...v];
            }
        }

        // Ensure value length matches time length
        for (let mt of Object.keys(o.vs)) {
            while ((o.vs[mt].length - 1) < o.ts.length) {
                o.vs[mt].push(o.vs[mt][o.vs[mt].length - 1]);
            }
        }

        // Mood (from Experience 21.js)
        if (t.hasOwnProperty("mood")) o.mood = t.mood.slice();

        if (loop) o.loop = loop;
        return o;
    }

    /**
     * Get pose template property with optional mirroring (from Experience 21.js)
     * @param {string} key - Property key
     * @return {Object} Cloned property value
     */
    getPoseTemplateProp(key) {
        let q;
        if (!this.poseWeightOnLeft) {
            if (key.startsWith('Left')) {
                key = 'Right' + key.substring(4);
            } else if (key.startsWith('Right')) {
                key = 'Left' + key.substring(5);
            }
            q = this.poseTarget.template.props[key].clone();
            if (q.isQuaternion) {
                q.x *= -1;
                q.w *= -1;
            }
        } else {
            q = this.poseTarget.template.props[key].clone();
        }
        return q;
    }

    /**
     * Inverse Kinematics solver (from Experience 21.js)
     * @param {Object} ik - IK configuration
     * @param {THREE.Vector3} target - Target position
     * @param {boolean} relative - Whether target is relative
     * @param {number} d - Duration
     */
    ikSolve(ik, target = null, relative = false, d = null) {
        const q = new THREE.Quaternion();
        const targetVec = new THREE.Vector3();
        const effectorPos = new THREE.Vector3();
        const effectorVec = new THREE.Vector3();
        const linkPos = new THREE.Vector3();
        const invLinkQ = new THREE.Quaternion();
        const linkScale = new THREE.Vector3();
        const axis = new THREE.Vector3();
        const vector = new THREE.Vector3();

        // Reset IK setup positions and rotations
        const root = this.ikMesh.getObjectByName(ik.root);
        root.position.setFromMatrixPosition(this.armature.getObjectByName(ik.root).matrixWorld);
        root.quaternion.setFromRotationMatrix(this.armature.getObjectByName(ik.root).matrixWorld);
        if (target && relative) {
            target.add(root.position);
        }
        const effector = this.ikMesh.getObjectByName(ik.effector);
        const links = ik.links;
        links.forEach(x => {
            x.bone = this.ikMesh.getObjectByName(x.link);
            x.bone.quaternion.copy(this.getPoseTemplateProp(x.link + '.quaternion'));
        });
        root.updateMatrixWorld(true);
        const iterations = ik.iterations || 10;

        // Iterate
        if (target) {
            for (let i = 0; i < iterations; i++) {
                let rotated = false;
                for (let j = 0, jl = links.length; j < jl; j++) {
                    const bone = links[j].bone;
                    bone.matrixWorld.decompose(linkPos, invLinkQ, linkScale);
                    invLinkQ.invert();
                    effectorPos.setFromMatrixPosition(effector.matrixWorld);
                    effectorVec.subVectors(effectorPos, linkPos);
                    effectorVec.applyQuaternion(invLinkQ);
                    effectorVec.normalize();
                    targetVec.subVectors(target, linkPos);
                    targetVec.applyQuaternion(invLinkQ);
                    targetVec.normalize();
                    let angle = targetVec.dot(effectorVec);
                    if (angle > 1.0) {
                        angle = 1.0;
                    } else if (angle < -1.0) {
                        angle = -1.0;
                    }
                    angle = Math.acos(angle);
                    if (angle < 1e-5) continue;
                    if (links[j].minAngle !== undefined && angle < links[j].minAngle) {
                        angle = links[j].minAngle;
                    }
                    if (links[j].maxAngle !== undefined && angle > links[j].maxAngle) {
                        angle = links[j].maxAngle;
                    }
                    axis.crossVectors(effectorVec, targetVec);
                    axis.normalize();
                    q.setFromAxisAngle(axis, angle);
                    bone.quaternion.multiply(q);

                    // Constraints
                    bone.rotation.setFromVector3(vector.setFromEuler(bone.rotation).clamp(new THREE.Vector3(
                        links[j].minx !== undefined ? links[j].minx : -Infinity,
                        links[j].miny !== undefined ? links[j].miny : -Infinity,
                        links[j].minz !== undefined ? links[j].minz : -Infinity
                    ), new THREE.Vector3(
                        links[j].maxx !== undefined ? links[j].maxx : Infinity,
                        links[j].maxy !== undefined ? links[j].maxy : Infinity,
                        links[j].maxz !== undefined ? links[j].maxz : Infinity
                    )));

                    bone.updateMatrixWorld(true);
                    rotated = true;
                }
                if (!rotated) break;
            }
        }

        // Apply
        if (d) {
            links.forEach(x => {
                this.poseTarget.props[x.link + ".quaternion"].copy(x.bone.quaternion);
                this.poseTarget.props[x.link + ".quaternion"].t = this.animClock;
                this.poseTarget.props[x.link + ".quaternion"].d = d;
            });
        }
    }

    /**
     * Set mood and initialize baseline animations (from Experience 21.js)
     * @param {string} moodName Mood name
     */
    setMood(moodName) {
        moodName = (moodName || '').trim().toLowerCase();
        if (!this.animMoods || !this.animMoods.hasOwnProperty(moodName)) {
            if (this.animMoods && this.animMoods['neutral']) {
                console.warn('Unknown mood:', moodName, '- using neutral');
                moodName = 'neutral';
            } else {
                console.warn('No animMoods loaded from config, skipping setMood');
                return;
            }
        }

        this.moodName = moodName;
        this.mood = this.animMoods[this.moodName];

        if (!this.mood || this.morphs.length === 0) return;

        // Reset baseline to mood baseline (from Experience 21.js)
        if (this.mood.baseline) {
            // Reset all morph targets to baseline
            const allMorphs = Object.keys(this.morphs[0].morphTargetDictionary);
            allMorphs.forEach(mt => {
                this.setBaselineValue(mt, this.mood.baseline.hasOwnProperty(mt) ?
                    this.mood.baseline[mt] : 0);
            });
        }

        // Initialize mood animations (from Experience 21.js)
        if (this.mood.anims) {
            this.mood.anims.forEach(x => {
                let i = this.animQueue.findIndex(y => y.template.name === x.name);
                if (i !== -1) {
                    this.animQueue.splice(i, 1);
                }
                this.animQueue.push(this.animFactory(x, -1));
            });
        }
    }

    animate(t) {
        requestAnimationFrame((time) => this.animate(time));

        // Update the animation mixer with delta (from Experience 21.js)
        const mixerUpdateDelta = this.clock.getDelta();

        if (this.mixer) {
            this.mixer.update(mixerUpdateDelta);
        }

        // Calculate time difference (from Experience 21.js)
        let dt = t - this.animTimeLast;
        if (dt < this.animFrameDur) return;
        dt = dt / this.animSlowdownRate;
        this.animClock += dt;
        this.animTimeLast = t;

        // Update pose base (from Experience 21.js)
        if (this.poseAvatar && this.poseTarget && this.poseDelta) {
            this.updatePoseBase(this.animClock);
        }

        // Update pose delta (from Experience 21.js)
        if (this.poseAvatar && this.poseTarget && this.poseDelta) {
            this.updatePoseDelta();
        }

        // Randomized morph targets for natural variation (from Experience 21.js)
        if (this.morphs.length > 0 && this.randomized && this.randomized.length > 0) {
            const randomizedMs = this.randomized[Math.floor(Math.random() * this.randomized.length)];
            const v = this.getValue(randomizedMs);
            const vb = this.getBaselineValue(randomizedMs);
            if (v === vb) {
                const randomizedV = (this.mood?.baseline?.[randomizedMs] || 0) + Math.random() / 5;
                this.setBaselineValue(randomizedMs, randomizedV);
            }
        }

        // Process animation baseline (smooth transitions to baseline values)
        const o = {};

        for (let [mt, x] of Object.entries(this.animBaseline)) {
            const v = this.getValue(mt);
            const restrain = (this.isSpeaking && mt.startsWith("mouth")) ? 3 : 1;
            const target = x.target / restrain;

            if (v !== target) {
                if (x.t0 === undefined) {
                    x.t0 = this.animClock;
                    x.v0 = v;
                }
                const delay = 1000;
                o[mt] = this.valueAnimationSeq([x.t0, x.t0 + delay], [x.v0, target],
                    this.animClock, this.easing);
            } else {
                x.t0 = undefined;
            }
        }

        // Process animation queue (from Experience 21.js)
        for (let i = 0; i < this.animQueue.length; i++) {
            const x = this.animQueue[i];
            if (this.animClock >= x.ts[0]) {
                for (let [mt, vs] of Object.entries(x.vs)) {
                    // Skip smile morphs when speaking
                    if (this.isSpeaking && (
                        mt === this.mapping.mouthSmile ||
                        mt === "ShapeKey_Smile" ||
                        mt === "smile02.001"
                    )) {
                        continue;
                    }

                    // Handle special morph types (from Experience 21.js)
                    if (mt === 'speak') {
                        o[mt] = (o.hasOwnProperty(mt) ? o[mt] + ' ' + vs : "" + vs);
                        delete x.vs[mt];
                    } else if (mt === 'pose') {
                        o[mt] = "" + vs[1];
                        delete x.vs[mt];
                    } else if (mt === 'moveto' || mt === 'handLeft' || mt === 'handRight') {
                        for (let j = 0; j < x.ts.length; j++) {
                            if (vs[j] && this.animClock >= x.ts[j]) {
                                o[mt] = Object.assign(o[mt] || {}, vs[j]);
                                vs[j] = null;
                            }
                        }
                    } else {
                        if (vs[0] === null) vs[0] = this.getValue(mt);
                        o[mt] = this.valueAnimationSeq(x.ts, vs, this.animClock, this.easing);
                        if (this.animBaseline.hasOwnProperty(mt)) this.animBaseline[mt].t0 = undefined;
                        // Remove from previous animations
                        for (let j = 0; j < i; j++) {
                            if (this.animQueue[j].vs.hasOwnProperty(mt)) delete this.animQueue[j].vs[mt];
                        }
                    }
                }

                // Check if animation completed
                if (this.animClock >= x.ts[x.ts.length - 1]) {
                    // Handle mood change (from Experience 21.js)
                    if (x.hasOwnProperty('mood')) this.setMood(x.mood);

                    if (x.loop) {
                        const restrain = (this.isSpeaking &&
                            (x.template.name === 'head' || x.template.name === 'eyes')) ? 4 : 1;
                        this.animQueue[i] = this.animFactory(x.template,
                            (x.loop > 0 ? x.loop - 1 : x.loop), 1, 1 / restrain);
                    } else {
                        this.animQueue.splice(i--, 1);
                    }
                }
            }
        }

        // Set fixed morph targets (from Experience 21.js)
        for (let [mt, x] of Object.entries(this.animFixed)) {
            const v = this.getValue(mt);
            if (v !== x.target) {
                if (x.t0 === undefined) {
                    x.t0 = this.animClock;
                    x.v0 = v;
                }
                const delay = 1000;
                o[mt] = this.valueAnimationSeq([x.t0, x.t0 + delay], [x.v0, x.target], this.animClock, this.easing);
            } else {
                if (o.hasOwnProperty(mt)) delete o[mt];
                x.t0 = undefined;
            }
            if (this.animBaseline.hasOwnProperty(mt)) this.animBaseline[mt].t0 = undefined;
        }

        // Update values - Handle special morph types (from Experience 21.js)
        for (let [mt, x] of Object.entries(o)) {
            if (mt === 'speak') {
                this.speakText("" + x);
            } else if (mt === 'pose') {
                this.poseName = "" + x;
                this.setPoseFromTemplate(this.poseTemplates[this.poseName]);
            } else if (mt === 'moveto') {
                Object.entries(x.props).forEach(e => {
                    if (e[1]) {
                        this.poseTarget.props[e[0]].copy(e[1]);
                    } else {
                        this.poseTarget.props[e[0]].copy(this.getPoseTemplateProp(e[0]));
                    }
                    this.poseTarget.props[e[0]].t = this.animClock;
                    this.poseTarget.props[e[0]].d = (e[1] && e[1].d) ? e[1].d : (x.duration || 2000);
                });
            } else if (mt === 'handLeft') {
                this.ikSolve({
                    iterations: 20, root: "LeftShoulder", effector: "LeftHandMiddle1",
                    links: [
                        { link: "LeftHand", minx: -0.5, maxx: 0.5, miny: -1, maxy: 1, minz: -0.5, maxz: 0.5 },
                        { link: "LeftForeArm", minx: -0.5, maxx: 1.5, miny: -1.5, maxy: 1.5, minz: -0.5, maxz: 3 },
                        { link: "LeftArm", minx: -1.5, maxx: 1.5, miny: 0, maxy: 0, minz: -1, maxz: 3 }
                    ]
                }, x.x ? new THREE.Vector3(x.x, x.y, x.z) : null, true, x.d);
            } else if (mt === 'handRight') {
                this.ikSolve({
                    iterations: 20, root: "RightShoulder", effector: "RightHandMiddle1",
                    links: [
                        { link: "RightHand", minx: -0.5, maxx: 0.5, miny: -1, maxy: 1, minz: -0.5, maxz: 0.5, maxAngle: 0.1 },
                        { link: "RightForeArm", minx: -0.5, maxx: 1.5, miny: -1.5, maxy: 1.5, minz: -3, maxz: 0.5, maxAngle: 0.2 },
                        { link: "RightArm", minx: -1.5, maxx: 1.5, miny: 0, maxy: 0, minz: -1, maxz: 3 }
                    ]
                }, x.x ? new THREE.Vector3(x.x, x.y, x.z) : null, true, x.d);
            } else {
                this.setValue(mt, x);
            }
        }

        this.renderer.render(this.scene, this.camera);
    }

    /**
     * Update configuration options dynamically
     * @param {Object} options - Configuration options to update
     */
    updateOptions(options) {
        Object.assign(this.opt, options);

        // Apply updates that require immediate changes
        if (options.hasOwnProperty('cameraRotateEnable')) {
            this.controls.enabled = this.opt.cameraRotateEnable;
        }
        if (options.hasOwnProperty('cameraPanEnable')) {
            this.controls.enablePan = this.opt.cameraPanEnable;
        }
        if (options.hasOwnProperty('cameraZoomEnable')) {
            this.controls.enableZoom = this.opt.cameraZoomEnable;
        }
        if (options.hasOwnProperty('modelPixelRatio')) {
            this.renderer.setPixelRatio(this.opt.modelPixelRatio * window.devicePixelRatio);
        }
        if (options.hasOwnProperty('avatarMood')) {
            this.setMood(this.opt.avatarMood);
        }
        if (options.hasOwnProperty('modelFPS')) {
            this.animFrameDur = 1000 / this.opt.modelFPS;
        }

        // Update lights (from Experience 21.js)
        if (this.lightAmbient) {
            if (options.hasOwnProperty('lightAmbientColor')) {
                this.lightAmbient.color.set(new THREE.Color(this.opt.lightAmbientColor));
            }
            if (options.hasOwnProperty('lightAmbientIntensity')) {
                this.lightAmbient.intensity = this.opt.lightAmbientIntensity;
                this.lightAmbient.visible = (this.opt.lightAmbientIntensity !== 0);
            }
        }

        if (this.lightDirect) {
            if (options.hasOwnProperty('lightDirectColor')) {
                this.lightDirect.color.set(new THREE.Color(this.opt.lightDirectColor));
            }
            if (options.hasOwnProperty('lightDirectIntensity')) {
                this.lightDirect.intensity = this.opt.lightDirectIntensity;
                this.lightDirect.visible = (this.opt.lightDirectIntensity !== 0);
            }
            if (options.hasOwnProperty('lightDirectPhi') || options.hasOwnProperty('lightDirectTheta')) {
                this.lightDirect.position.setFromSphericalCoords(
                    2,
                    this.opt.lightDirectPhi,
                    this.opt.lightDirectTheta
                );
            }
        }

        if (this.lightSpot) {
            if (options.hasOwnProperty('lightSpotColor')) {
                this.lightSpot.color.set(new THREE.Color(this.opt.lightSpotColor));
            }
            if (options.hasOwnProperty('lightSpotIntensity')) {
                this.lightSpot.intensity = this.opt.lightSpotIntensity;
                this.lightSpot.visible = (this.opt.lightSpotIntensity !== 0);
            }
            if (options.hasOwnProperty('lightSpotPhi') || options.hasOwnProperty('lightSpotTheta')) {
                this.lightSpot.position.setFromSphericalCoords(
                    2,
                    this.opt.lightSpotPhi,
                    this.opt.lightSpotTheta
                );
            }
            if (options.hasOwnProperty('lightSpotDispersion')) {
                this.lightSpot.angle = this.opt.lightSpotDispersion;
            }
        }
    }

    /**
     * Get current configuration options
     * @returns {Object} Current opt configuration
     */
    getOptions() {
        return { ...this.opt };
    }

    /**
     * Mute/unmute avatar audio
     * @param {boolean} mute - Whether to mute the avatar
     */
    setMute(mute) {
        this.opt.avatarMute = mute;
        if (this.currentSpeechAudio) {
            this.currentSpeechAudio.muted = mute;
        }
    }

    /**
     * Switch to a different avatar
     * @param {string} avatarName - Name of the avatar to switch to
     */
    async switchAvatar(avatarName) {
        if (this.avatarName === avatarName) {
            return;
        }

        // Stop any current speech
        this.stopSpeaking();

        // Update avatar name
        this.avatarName = avatarName;
        localStorage.setItem('avatar_name', avatarName);

        // Load new avatar configuration
        await this.loadAvatarConfig();

        // Remove current character from scene
        if (this.character) {
            this.scene.remove(this.character);
        }

        // Clear morphs array to ensure new avatar's morphs are used
        this.morphs = [];

        // Clear animation mixer
        if (this.mixer) {
            this.mixer.stopAllAction();
            this.mixer = null;
        }

        // Reload the model
        this.loadModel();
    }
}

// Initialize the avatar application
const avatarApp = new AvatarApplication();
